/**
 * 屏 4 — 战斗执行中
 *
 * demo-gdd 附录 B 屏 4：
 *   - 顶部状态栏：TICK / PHASE / LIVE
 *   - 主区：
 *     - 左：玩家卡 (HP / KDA / 状态) + 左阵营流式信息 stream-a
 *     - 中：Canvas 战场（25×25 demo override，自适应缩放）
 *     - 右：AI 卡 + 右阵营流式信息 stream-b
 *     - 阵营流:KILL FEED 拆为 stream-a(team_a) / stream-b(team_b),半透明浮层 + 收起/展开
 *   - 底栏：SPEED 已迁至 head 栏；MATCH STATS 已移除；CONSOLE 多标签栏在 battlefield 下方
 *   - 屏 4 进入时 auto-commit（Q15=B）
 *
 * v2 (2026-07-10)：V2 改造 — 接入 LevelStore
 *   - 新增 loading_progress 事件监听
 *   - 区分 error.source（player → [RUNTIME_ERR] 红色 / ai → [AI_RUNTIME_ERR] 紫色 #9B59B6）
 *   - CONSOLE 面板新增 AI_RUNTIME_ERR 紫色
 *   - AI 异常只进 stream-b，玩家异常只进 stream-a
 *   - 兵牌信息从 LevelStore 读取（名称/AI名）
 *   - 移除地图选择引用
 */

import { CanvasRenderer, type RenderOptions } from "../render/canvas-renderer";
import { EngineScheduler } from "../engine/engine-scheduler";
import { svgLoader, BATTLE_SVG_KEYS } from "../utils/svg-loader";
import {
  type BattleState,
  type BotAction,
  type EngineEvent,
  type Position,
  type TileType,
  type VfxFrameEvents,
} from "../engine/types";
import { getProject, getEffectiveMapId } from "../storage/project-store";
import { getLevelStore } from "../storage/level-store";
import { pathFor } from "../router/screen-router";
import { pushCommit } from "../services/git-service";
// P1-#6:Give Up 用内嵌 modal 替代 window.confirm
import { showConfirmModal } from "./modal-helper";
// P0-#12:从 data/maps/ 加载 25×25 地图(之前硬编码全 empty)
// GDD §12 LOCK:24 cover + 12 obstacle + 4 spawn + 4 unreachable + 581 empty = 625
// V2(2026-07-10):selectedMapId 走 getEffectiveMapId(project),不再依赖屏 3 sessionStorage
//   优先级:mapId > levelId > mapRef > "battle_easy"(老项目兜底)
import { getMapById, extractCoverPositions, extractAllTilePositions, registerRuntimeMap } from "../engine/map-utils";
import { loadRules } from "../engine/rules-loader";
import { NUM_TO_TILE, CHAR_TO_TILE } from "../engine/tile-codec";
// FR-018 ADR-018.1:HP 节点切分 PHASE 数值从 TANK_STATS 读,不再 hardcode 38/75
import { TANK_STATS, computePhase as computePhaseFromStats, chebyshevDistance } from "../engine/tank-stats";
// v2: SplitPanel 拖拽分割器
import { SplitPanel } from "../ui/split-panel";
import { TOKEN_MAP } from "../utils/color-tokens";
import { onThemeChange } from "../services/theme-service";
import { t } from "../i18n";
// V5 P1: 术语替换 + 引导浮层
import { FEEDBACK_TIPS, showFeedbackTip } from "../ui/battle-feedback";
// V17: 战斗教学状态（hintDensity + unlockedApis）传递
import { getBattleTeachingState } from "../onboarding/battle-teaching-state";
// V14: 部署加载遮罩（屏 2 提交出战 → 屏 4 引擎就绪全链路过渡）
import { hideDeployLoading, updateDeployLoading } from "../ui/deploy-loading";
// V15 音频引擎
import { audioEngine } from "../services/audio-engine";

/**
 * V3: 根据队伍和坦克索引获取坦克位置（支持多坦克模式）
 * 用于替代直接访问 currentState.self.position / currentState.enemy.position
 */
function getTankPosition(state: BattleState, team: "team_a" | "team_b", index: number): Position | null {
  const teamState = state[team];
  if (!teamState?.tanks || index >= teamState.tanks.length) return null;
  const tank = teamState.tanks[index];
  if (!tank.is_alive) return null;
  return tank.position;
}

/** V5 P1: 检测并显示引导浮层 */
function checkAndShowFeedbackTips(
  state: BattleState,
  _event: any,
  container: HTMLElement,
): void {
  // V17: hintDensity 门控 — 考核关(0)静默，密级 1+ 才显示浮层
  const teachingState = getBattleTeachingState();
  const density = teachingState?.hintDensity ?? 5;
  if (density === 0) return;   // 考核关完全静默
  if (density === 1) return;   // 1.2.x 不弹反馈浮层
  // density 3+ 正常显示

  const tick = _event.tickNum ?? 0;
  if (tick < 2) return;

  // V17: 新能力解锁提示 — 首次进入战斗时显示本关解锁的 API
  if (tick === 2 && teachingState?.unlockedApis?.length) {
    const apiNames = teachingState.unlockedApis.map((a: string) => `self.${a}`).join(" · ");
    showFeedbackTip(container, {
      id: `unlock-${teachingState.levelId}`,
      text: `新能力解锁：${apiNames}`,
      duration: 4000,
    });
  }

  const player = state.self;
  if (!player?.is_alive) return;

  // 首次扫描
  if (state.enemy?.is_visible && tick < 15) {
    showFeedbackTip(container, FEEDBACK_TIPS.first_scan);
  }

  // 血量低于安全线
  if (player.health < 50 && player.health > 0) {
    showFeedbackTip(container, FEEDBACK_TIPS.hp_low);
  }

  // 冷却就绪且对手可见
  if (player.cooldown_left === 0 && state.enemy?.is_visible) {
    showFeedbackTip(container, FEEDBACK_TIPS.cooldown_ready);
  }

  // 对手可见（首次）
  if (state.enemy?.is_visible && tick > 10 && tick < 30) {
    showFeedbackTip(container, FEEDBACK_TIPS.enemy_visible);
  }

  // 接近对手
  if (state.enemy?.is_visible && player.position && state.enemy.position) {
    const dx = Math.abs(player.position.x - state.enemy.position.x);
    const dy = Math.abs(player.position.y - state.enemy.position.y);
    if (dx + dy <= 3) {
      showFeedbackTip(container, FEEDBACK_TIPS.near_enemy);
    }
  }
}

type Speed = "pause" | "1x" | "2x" | "4x";
const SPEED_TO_MULT: Record<Speed, number> = { pause: 0, "1x": 1, "2x": 2, "4x": 4 };

// P1 修复:CANVAS_CELL 必须在文件顶部声明,避免 resizeCanvas() (L460) 在 const 之前访问触发 TDZ
// FR-026(2026-07-08): cellSize 24→32 对齐 GDD §12.2 锁值(1 tile = 32px, 战斗画布 800×800)
const CANVAS_CELL = 32; // 与 CanvasRenderer.cellSize 同步
let screen4Disposed = false;
let readyResolve: (() => void) | null = null;
const readyTimerIds: number[] = [];

interface StreamEntry {
  tick: number;
  text: string;
  // FR-026(2026-07-08): 加 "scan", 让 scan 动作也能进 stream(之前类型联合漏了, tsc 报错)
  type: "kill" | "shoot" | "scan" | "move" | "error" | "phase" | "think";
}


export async function renderScreen4Battle(
  root: HTMLElement,
  projectId: string,
  onNavigate: (path: string) => void,
  isActive: () => boolean = () => true,
) {
  screen4Disposed = false;
  readyResolve = null;
  readyTimerIds.splice(0).forEach((id) => window.clearTimeout(id));
  // F1: mount 时清理上一局残留的 battle summary，确保本局不读旧值
  sessionStorage.removeItem(`battle-${projectId}`);

  const project = getProject(projectId);
  if (!project) {
    root.innerHTML = `<div class="error">项目不存在：${projectId}</div>`;
    return;
  }

  // ===== 屏 4 Auto-commit（Q15=B）—— 真实 git 集成 =====
  // 之前只 localStorage 改 commitCount；现在通过 git-service.pushCommit 写入真实历史
  pushCommit(
    projectId,
    project.code,
    `auto-commit #${project.commitCount + 1}: pre-battle snapshot`,
  );

  // V2(2026-07-10):selectedMapId / 兵牌名 / aiCode 全部从 LevelStore.load 自取,
  //   不再依赖屏 3 sessionStorage 中转(屏 3 已移除,5 屏架构)
  //   - selectedMapId 走 getEffectiveMapId(project) helper(优先级 mapId > levelId > mapRef > "battle_easy")
  //   - aiBotName / levelDisplayName / aiCode 在下方 LevelStore.load 完成后赋值
  const playerBotName = "tank_v1.py";
  let aiBotName = "ai_easy.py";
  let levelDisplayName = "battle_easy";
  let aiCode: string | undefined = undefined;

  const selectedMapId: string = getEffectiveMapId(project);

  // ★ FR-025 修复:优先从 LevelStore 动态加载(编辑器的保存立即可见),
  //   静态 import 在构建时快照,编辑器保存后不更新
  let battleMap: any = getMapById(selectedMapId);
  let levelTiles: TileType[] | null = null;
  
  // V16: 读取关卡 manifest 的 enemy_hp_override（新手引导第零章用）
  let enemyHpOverride: number | null = null;
  if (project.levelId || project.mapRef) {
    const levelId = (project.mapRef || project.levelId)!;
    try {
      const levelData = await getLevelStore().load(levelId);
      if (!isActive()) return;
      // V16: 读取 enemy_hp_override
      const gameConfig = (levelData as any)?.game_config || (levelData as any)?.manifest?.game_config;
      if (gameConfig?.enemy_hp_override) {
        enemyHpOverride = gameConfig.enemy_hp_override;
      }
      levelTiles = v2LevelTiles(levelData);
      // V2:兵牌名/AI名/aiCode 从 LevelStore 取(比 sessionStorage 更可靠,不依赖屏 3 写入)
      aiBotName = levelData.aiName || aiBotName;
      levelDisplayName = levelData.name || levelDisplayName;
      aiCode = levelData.aiCode || undefined;
        // 用 LevelStore 的 spawn/dimensions 覆盖静态 import
        if (levelData.map) {
          // V3: 优先用 spawnsA/spawnsB 完整数组（3v3 多 spawn），fallback 到 spawnA/spawnB 单点
          const aSpawns: Array<{x:number;y:number}> =
            (levelData.map as any).spawnsA?.map((p: [number, number]) => ({ x: p[0], y: p[1] })) ??
            [{ x: levelData.map.spawnA[0], y: levelData.map.spawnA[1] }];
          const bSpawns: Array<{x:number;y:number}> =
            (levelData.map as any).spawnsB?.map((p: [number, number]) => ({ x: p[0], y: p[1] })) ??
            [{ x: levelData.map.spawnB[0], y: levelData.map.spawnB[1] }];
          battleMap = {
            dimensions: { width: levelData.map.width, height: levelData.map.height },
            spawns: { team_a: aSpawns, team_b: bSpawns },
            tiles: [] as string[],  // 不再用 string[] 格式
          };
        // ★ 注入 runtime registry：让 engine-scheduler 30Hz tick 中的
        //   extractCoverPositions(mapId) / isObstacleAt(x,y,mapId) 能拿到 _user 关卡数据
        registerRuntimeMap(levelId, levelData.map);
      }
    } catch (e) {
      console.warn('[screen-4] LevelStore load failed, fallback to static import:', (e as Error).message);
    }
  }

  const mapSpawnA = (battleMap.spawns.team_a[0] ?? { x: 0, y: 12 }) as { x: number; y: number };
  const mapSpawnB = (battleMap.spawns.team_b[0] ?? { x: 24, y: 12 }) as { x: number; y: number };

  // ===== 加载地图 tiles（屏 4 init）=====
  const tiles = levelTiles ?? await loadMapTiles(selectedMapId);
  if (!isActive()) return;

  // FR-018 ADR-018.8:从 map JSON 自动提取 cover positions,不再 hardcode
  const coverPositions = extractCoverPositions(selectedMapId);
  // P0 hover info:拿全 4 类 tile 位置(obstacle/spawn/unreachable),tooltip 判 type 用
  const allTilePositions = extractAllTilePositions(selectedMapId);
  // 取玩家 spawn 位置最近的 cover(切比雪夫距离最小)
  // L1 地图(0 cover)→ nearestCoverPos 保持 fallback,covers 为空时引擎会处理
  let nearestCoverPos: Position = { x: mapSpawnA.x + 4, y: mapSpawnA.y - 1 };
  let nearestCoverDist = Infinity;
  for (const cp of coverPositions) {
    const cx = cp[0];
    const cy = cp[1];
    const d = chebyshevDistance({ x: mapSpawnA.x, y: mapSpawnA.y }, { x: cx, y: cy });
    if (d < nearestCoverDist) {
      nearestCoverDist = d;
      nearestCoverPos = { x: cx, y: cy };
    }
  }
  // 0 cover 地图(L1)用 Infinity 标记,battleState.nearest_cover = null
  if (coverPositions.length === 0) {
    nearestCoverDist = Infinity;
  }

  // ===== 初始 BattleState（player @ (0,12)，enemy @ (24,12)）=====
  // FR-018 ADR-018.2:enemy 加 is_alive/max_health 字段（与 self 对称）
  // FR-018 P0-#6:数值用 TANK_STATS,不再 hardcode 150
  // FR-022 ADR-022.2:self/enemy 加 spawn 字段,供 self.spawn_point / self.enemy_spawn_point 读取
  const initialState: BattleState = {
    tick: 0,
    map_id: selectedMapId,
    map_size: { width: (battleMap.dimensions?.width ?? 25), height: (battleMap.dimensions?.height ?? 25) },
    self: {
      team: "team_a",
      tank_index: 0,
      position: { x: mapSpawnA.x, y: mapSpawnA.y },
      spawn: { x: mapSpawnA.x, y: mapSpawnA.y }, // FR-022 ADR-022.2:玩家出生点(开局固定)
      health: TANK_STATS.max_health,
      max_health: TANK_STATS.max_health,
      cooldown_left: 0,
      attack_cooldown_ticks: TANK_STATS.attack_cooldown_ticks,
      is_alive: true,
    },
    enemy: {
      tank_index: 0,
      position: { x: mapSpawnB.x, y: mapSpawnB.y },
      spawn: { x: mapSpawnB.x, y: mapSpawnB.y }, // FR-022 ADR-022.2:AI 出生点(开局固定)
      health: enemyHpOverride ?? TANK_STATS.max_health,
      max_health: enemyHpOverride ?? TANK_STATS.max_health,
      is_alive: true,
      // FR-022 ADR-022.1:spawn 距离 > vision_range=10 → 双方都看不到对方,需主动 scan
      is_visible: false,
      last_known_position: null,
    },
    // 0 cover 地图(L1)用 null
    nearest_cover: coverPositions.length === 0 ? null : { position: nearestCoverPos, distance: nearestCoverDist },
    ai_known_player_position: null, // P0 修复:AI 视角单独追踪玩家位置
  };

  // ===== V3: 多坦克模式 — 构建 team_a/team_b =====
  const rules = loadRules(selectedMapId);
  const unitsPerTeam = rules.constraints?.max_units_per_team ?? 1;
  if (unitsPerTeam > 1) {
    // 从 map spawns 构建 team
    const spawnsA = battleMap.spawns?.team_a ?? [{ x: mapSpawnA.x, y: mapSpawnA.y }];
    const spawnsB = battleMap.spawns?.team_b ?? [{ x: mapSpawnB.x, y: mapSpawnB.y }];
    initialState.team_a = {
      tanks: Array.from({ length: unitsPerTeam }, (_, i) => ({
        team: "team_a" as const,
        tank_index: i,
        position: { x: spawnsA[i]?.x ?? mapSpawnA.x, y: spawnsA[i]?.y ?? mapSpawnA.y },
        spawn: { x: spawnsA[i]?.x ?? mapSpawnA.x, y: spawnsA[i]?.y ?? mapSpawnA.y },
        health: TANK_STATS.max_health,
        max_health: TANK_STATS.max_health,
        cooldown_left: 0,
        attack_cooldown_ticks: TANK_STATS.attack_cooldown_ticks,
        is_alive: true,
      })),
      units_per_team: unitsPerTeam,
    };
    initialState.team_b = {
      tanks: Array.from({ length: unitsPerTeam }, (_, i) => ({
        team: "team_b" as const,
        tank_index: i,
        position: { x: spawnsB[i]?.x ?? mapSpawnB.x, y: spawnsB[i]?.y ?? mapSpawnB.y },
        spawn: { x: spawnsB[i]?.x ?? mapSpawnB.x, y: spawnsB[i]?.y ?? mapSpawnB.y },
        health: TANK_STATS.max_health,
        max_health: TANK_STATS.max_health,
        cooldown_left: 0,
        attack_cooldown_ticks: TANK_STATS.attack_cooldown_ticks,
        is_alive: true,
      })),
      units_per_team: unitsPerTeam,
    };
    // 同步 self/enemy 到 tanks[0]
    initialState.self = { ...initialState.team_a.tanks[0] };
    initialState.enemy = {
      tank_index: 0,
      position: { ...initialState.team_b.tanks[0].position },
      spawn: { ...initialState.team_b.tanks[0].spawn },
      health: initialState.team_b.tanks[0].health,
      max_health: initialState.team_b.tanks[0].max_health,
      is_alive: true,
      is_visible: false,
      last_known_position: null,
    };
  }

  // P1 修复:currentState 必须在 initialState 之后立即声明,避免 resizeCanvas() (L460) 在 let 之前访问触发 TDZ
  let currentState: BattleState = initialState;

  // ===== DOM 骨架 (v2: Flexbox 面板容器 + 3 grip 分割线) =====
  if (!isActive()) return;
  root.innerHTML = `
    <div class="s4-root" id="battle-root">
      <!-- Head Bar (48px, 三段式) -->
      <div class="s4-head" id="head-bar">
        <button class="s4-head__btn" id="btn-give-up">${t("battle.give_up")}</button>
        <div class="s4-spacer"></div>
        <div class="s4-pill" id="pill-time">
          <span class="s4-pill__label">${t("battle.time")}</span>
          <span class="s4-pill__value" id="val-time">00:00/01:30</span>
        </div>
        <div class="s4-pill s4-pill--tick" id="pill-tick">
          <span class="s4-pill__label">${t("battle.tick")}</span>
          <span class="s4-pill__value" id="val-tick">0000</span>
          <span class="s4-pill__label s4-pill__label--dim">/2700</span>
        </div>
        <div class="s4-spd" id="spd-ctrl">
          <span class="s4-spd__lbl">${t("battle.speed")}</span>
          <button class="s4-spd__btn" data-speed="1x">1×</button>
          <button class="s4-spd__btn s4-spd__btn--active" data-speed="2x">2×</button>
          <button class="s4-spd__btn" data-speed="4x">4×</button>
          <button class="s4-spd__btn" data-speed="pause">⏸</button>
        </div>
        <!-- V14: VFX 特效开关 -->
        <div class="s4-vfx" id="vfx-ctrl">
          <button class="s4-vfx__btn" id="btn-vfx-toggle">VFX ▾</button>
          <div class="s4-vfx__panel" id="vfx-panel" style="display:none">
            <label class="vfx-row"><span>Grid</span><input type="checkbox" checked data-vfx="showGrid" /></label>
            <label class="vfx-row"><span>Vision</span><input type="checkbox" checked data-vfx="showVisionRange" /></label>
            <label class="vfx-row"><span>Shake</span><input type="checkbox" checked data-vfx="showScreenShake" /></label>
            <label class="vfx-row"><span>HitPause</span><input type="checkbox" checked data-vfx="showHitPause" /></label>
            <label class="vfx-row"><span>DMG#</span><input type="checkbox" checked data-vfx="showDamageNumbers" /></label>
            <label class="vfx-row"><span>Bullet</span><input type="checkbox" checked data-vfx="showProjectiles" /></label>
            <label class="vfx-row"><span>Fragment</span><input type="checkbox" checked data-vfx="showFragments" /></label>
            <label class="vfx-row"><span>HUD</span><input type="checkbox" checked data-vfx="showHud" /></label>
            <label class="vfx-row"><span>Line</span><input type="checkbox" checked data-vfx="showActionLines" /></label>
            <label class="vfx-row"><span>K.Feed</span><input type="checkbox" checked data-vfx="showKillFeed" /></label>
          </div>
        </div>
        <!-- 保留旧 DOM 的隐藏元素用于 JS 兼容 (status-live, tick-num, header-phase-bar, error-status) -->
        <span id="status-live" style="display:none;">● LIVE</span>
        <span id="tick-num" style="display:none;">0</span>
        <span class="error-status" id="error-status" hidden>[ERROR] · <b id="error-code">—</b></span>
        <div id="header-phase-bar" style="display:none;"></div>
      </div>

      <!-- Main Area (3 列 Flexbox) -->
      <div class="s4-main" id="battle-main">
        <!-- Left Sidebar: ALPHA -->
        <div class="s4-sidebar s4-sidebar--alpha" id="sidebar-alpha">
          <div class="sidebar-header sidebar-header--alpha">
            <div class="sidebar-header__label">
              <span class="sidebar-team-tag sidebar-team-tag--a">A</span>
              <span class="sidebar-header__title">${t("battle.team_a_title")}</span>
            </div>
            <span class="sidebar-header__op" id="sidebar-op-a" hidden>OP-01</span>
          </div>

          <section class="tank-log tank-log--alpha" id="stream-a" data-team="a">
            <header class="tank-log__head">
              <span class="tank-log__title">${t("battle.tank_log")}</span>
            </header>

            <article class="tank-entry tank-entry--expanded tank-entry--a" data-entry="a-1">
              <button class="tank-entry__header" id="stream-toggle-a" type="button">
                <span class="tank-entry__left">
                  <span class="tank-entry__dot"></span>
                  <span class="tank-entry__name" id="stream-title-a">${escapeHtml(playerBotName)}</span>
                </span>
                <span class="tank-entry__right">
                  <span class="tank-entry__hp" id="player-hp-num">150/150</span>
                  <span class="tank-entry__chevron">▾</span>
                </span>
              </button>
              <div class="tank-entry__detail">
                <div class="hp-bar" id="player-hp-bar" aria-label="血量条">
                  <div class="hp-bar-fill" style="width: 100%"></div>
                </div>
                <div class="kda-status-row">
                  <div class="kda">
                    <span class="kda-item"><b class="kda-value" id="player-k">0</b><span class="kda-hint">K</span></span>
                    <span class="kda-item"><b class="kda-value" id="player-d">0</b><span class="kda-hint">D</span></span>
                    <span class="kda-item kda-empty"><b class="kda-value">—</b><span class="kda-hint">A</span></span>
                  </div>
                  <div class="card-info">
                    <span class="status-badge status-idle" id="player-status-badge" title="Idle — 待命">
                      <span class="status-glyph" aria-hidden="true">●</span>
                      <span class="status-label" id="player-status-label">${t("battle.status_idle")}</span>
                    </span>
                  </div>
                </div>
                <div class="tank-stat-row">
                  <span class="tank-stat-pill" id="player-hit-pct">HIT 0%</span>
                  <span class="tank-stat-item"><span class="tank-stat-label">MOV</span><span class="tank-stat-val" id="player-mov">0</span></span>
                  <span class="tank-stat-item"><span class="tank-stat-label">SHO</span><span class="tank-stat-val" id="player-sho">0</span></span>
                  <span class="tank-stat-item"><span class="tank-stat-label">DMG</span><span class="tank-stat-val" id="player-dmg">0</span></span>
                </div>
                <div class="tank-pos-row" id="player-pos-row">POS (00,00)</div>
                <div class="tank-entry__log">
                  <ul class="stream-list" id="stream-list-a"></ul>
                </div>
              </div>
            </article>

            <article class="tank-entry tank-entry--collapsed tank-entry--a" data-entry="a-2">
              <button class="tank-entry__header" id="stream-toggle-a-2" type="button">
                <span class="tank-entry__left"><span class="tank-entry__dot"></span><span class="tank-entry__name" id="tank-a2-name">SLOT-02</span></span>
                <span class="tank-entry__right"><span class="tank-entry__hp" id="tank-a2-hp">--/--</span><span class="tank-entry__chevron" id="tank-a2-chevron">›</span></span>
              </button>
              <div class="tank-entry__detail" id="tank-a2-detail" hidden>
                <div class="hp-bar"><div class="hp-bar-fill" id="tank-a2-hp-bar" style="width: 100%"></div></div>
                <div class="kda-status-row">
                  <div class="kda">
                    <span class="kda-item"><b class="kda-value" id="tank-a2-k">0</b><span class="kda-hint">K</span></span>
                    <span class="kda-item"><b class="kda-value" id="tank-a2-d">0</b><span class="kda-hint">D</span></span>
                  </div>
                  <div class="card-info">
                    <span class="status-badge status-idle" id="tank-a2-status-badge">
                      <span class="status-glyph" aria-hidden="true">●</span>
                      <span class="status-label" id="tank-a2-status-label">待命</span>
                    </span>
                  </div>
                </div>
                <div class="tank-stat-row">
                  <span class="tank-stat-pill" id="tank-a2-hit-pct">HIT 0%</span>
                  <span class="tank-stat-item"><span class="tank-stat-label">MOV</span><span class="tank-stat-val" id="tank-a2-mov">0</span></span>
                  <span class="tank-stat-item"><span class="tank-stat-label">SHO</span><span class="tank-stat-val" id="tank-a2-sho">0</span></span>
                  <span class="tank-stat-item"><span class="tank-stat-label">DMG</span><span class="tank-stat-val" id="tank-a2-dmg">0</span></span>
                </div>
                <div class="tank-pos-row" id="tank-a2-pos-row">POS (00,00)</div>
                <div class="tank-entry__log">
                  <ul class="stream-list" id="tank-a2-stream-list"></ul>
                </div>
              </div>
            </article>

            <article class="tank-entry tank-entry--collapsed tank-entry--a" data-entry="a-3">
              <button class="tank-entry__header" id="stream-toggle-a-3" type="button">
                <span class="tank-entry__left"><span class="tank-entry__dot"></span><span class="tank-entry__name" id="tank-a3-name">SLOT-03</span></span>
                <span class="tank-entry__right"><span class="tank-entry__hp" id="tank-a3-hp">--/--</span><span class="tank-entry__chevron" id="tank-a3-chevron">›</span></span>
              </button>
              <div class="tank-entry__detail" id="tank-a3-detail" hidden>
                <div class="hp-bar"><div class="hp-bar-fill" id="tank-a3-hp-bar" style="width: 100%"></div></div>
                <div class="kda-status-row">
                  <div class="kda">
                    <span class="kda-item"><b class="kda-value" id="tank-a3-k">0</b><span class="kda-hint">K</span></span>
                    <span class="kda-item"><b class="kda-value" id="tank-a3-d">0</b><span class="kda-hint">D</span></span>
                  </div>
                  <div class="card-info">
                    <span class="status-badge status-idle" id="tank-a3-status-badge">
                      <span class="status-glyph" aria-hidden="true">●</span>
                      <span class="status-label" id="tank-a3-status-label">待命</span>
                    </span>
                  </div>
                </div>
                <div class="tank-stat-row">
                  <span class="tank-stat-pill" id="tank-a3-hit-pct">HIT 0%</span>
                  <span class="tank-stat-item"><span class="tank-stat-label">MOV</span><span class="tank-stat-val" id="tank-a3-mov">0</span></span>
                  <span class="tank-stat-item"><span class="tank-stat-label">SHO</span><span class="tank-stat-val" id="tank-a3-sho">0</span></span>
                  <span class="tank-stat-item"><span class="tank-stat-label">DMG</span><span class="tank-stat-val" id="tank-a3-dmg">0</span></span>
                </div>
                <div class="tank-pos-row" id="tank-a3-pos-row">POS (00,00)</div>
                <div class="tank-entry__log">
                  <ul class="stream-list" id="tank-a3-stream-list"></ul>
                </div>
              </div>
            </article>
          </section>
        </div>

        <!-- Grip 1: Sidebar Alpha ↔ Center -->
        <div class="s4-grip s4-grip--v" id="grip-left"></div>

        <!-- Center Column -->
        <div class="s4-center" id="center-col">
          <div class="s4-battlefield" id="battlefield">
            <!-- P2: 扫描线 -->
            <div class="battle-scan-line" id="battle-scan-line"></div>
            <!-- P2: FIRST BLOOD 覆盖层 -->
            <div class="battle-first-blood" id="battle-first-blood" style="display:none">${t("battle.first_blood")}</div>
            <!-- V14: 击杀标注 feed -->
            <div class="battle-kill-feed" id="battle-kill-feed"></div>
            <canvas id="battle-canvas" width="800" height="800"></canvas>
          </div>
          <!-- Grip 3: Battlefield ↔ Console -->
          <div class="s4-grip s4-grip--h" id="grip-console"></div>
          <div class="s4-console" id="console-area">
            <!-- Ardot 终稿: CONSOLE 头部 (▌CONSOLE 标题 + 标签 + 右侧计数) -->
            <div class="s4-console-header">
              <div class="s4-console-header__left">
                <span class="s4-console-title">${t("battle.console_title")}</span>
                <div class="s4-console-tabs" id="console-tabs">
                  <button class="s4-console-tab s4-console-tab--active" data-filter="all">${t("battle.console_tab_all")} <span class="tab-count" id="tab-count-all">0</span></button>
                  <button class="s4-console-tab" data-filter="team_a">${t("battle.console_tab_team_a")} <span class="tab-count" id="tab-count-team_a">0</span></button>
                  <button class="s4-console-tab" data-filter="team_b">${t("battle.console_tab_team_b")} <span class="tab-count" id="tab-count-team_b">0</span></button>
                  <button class="s4-console-tab" data-filter="kill">${t("battle.console_tab_kill")} <span class="tab-count" id="tab-count-kill">0</span></button>
                  <button class="s4-console-tab" data-filter="error">${t("battle.console_tab_error")} <span class="tab-count" id="tab-count-error">0</span></button>
                </div>
              </div>
              <span class="s4-console-visible" id="console-visible">—</span>
              <button class="s4-console-toggle" id="console-toggle" type="button" title="${t("battle.expand_console")}" aria-expanded="false" aria-label="${t("battle.expand_console")}">▴</button>
            </div>
            <!-- 日志列表 -->
            <div class="s4-console-log" id="console-log">
              <div class="s4-console-empty" id="console-empty">${t("battle.console_empty")}</div>
            </div>
          </div>
        </div>

        <!-- Grip 2: Center ↔ Sidebar Bravo -->
        <div class="s4-grip s4-grip--v" id="grip-right"></div>

        <!-- Right Sidebar: BRAVO -->
        <div class="s4-sidebar s4-sidebar--bravo" id="sidebar-bravo">
          <div class="sidebar-header sidebar-header--bravo">
            <div class="sidebar-header__label">
              <span class="sidebar-team-tag sidebar-team-tag--b">B</span>
              <span class="sidebar-header__title">${t("battle.team_b_title")}</span>
            </div>
            <span class="sidebar-header__op" id="sidebar-op-b" hidden>OP-02</span>
          </div>

          <section class="tank-log tank-log--bravo" id="stream-b" data-team="b">
            <header class="tank-log__head">
              <span class="tank-log__title">${t("battle.tank_log")}</span>
            </header>

            <article class="tank-entry tank-entry--expanded tank-entry--b" data-entry="b-1">
              <button class="tank-entry__header" id="stream-toggle-b" type="button">
                <span class="tank-entry__left">
                  <span class="tank-entry__dot"></span>
                  <span class="tank-entry__name" id="stream-title-b">${escapeHtml(aiBotName)}</span>
                </span>
                <span class="tank-entry__right">
                  <span class="tank-entry__hp" id="ai-hp-num">150/150</span>
                  <span class="tank-entry__chevron">▾</span>
                </span>
              </button>
              <div class="tank-entry__detail">
                <div class="hp-bar" id="ai-hp-bar" aria-label="血量条">
                  <div class="hp-bar-fill" style="width: 100%"></div>
                </div>
                <div class="kda-status-row">
                  <div class="kda">
                    <span class="kda-item"><b class="kda-value" id="ai-k">0</b><span class="kda-hint">K</span></span>
                    <span class="kda-item"><b class="kda-value" id="ai-d">0</b><span class="kda-hint">D</span></span>
                    <span class="kda-item kda-empty"><b class="kda-value">—</b><span class="kda-hint">A</span></span>
                  </div>
                  <div class="card-info">
                    <span class="status-badge status-idle" id="ai-status-badge" title="Idle — 待命">
                      <span class="status-glyph" aria-hidden="true">●</span>
                      <span class="status-label" id="ai-status-label">${t("battle.status_idle")}</span>
                    </span>
                  </div>
                </div>
                <div class="tank-stat-row">
                  <span class="tank-stat-pill" id="ai-hit-pct">HIT 0%</span>
                  <span class="tank-stat-item"><span class="tank-stat-label">MOV</span><span class="tank-stat-val" id="ai-mov">0</span></span>
                  <span class="tank-stat-item"><span class="tank-stat-label">SHO</span><span class="tank-stat-val" id="ai-sho">0</span></span>
                  <span class="tank-stat-item"><span class="tank-stat-label">DMG</span><span class="tank-stat-val" id="ai-dmg">0</span></span>
                </div>
                <div class="tank-pos-row" id="ai-pos-row">POS (00,00)</div>
                <div class="tank-entry__log">
                  <ul class="stream-list" id="stream-list-b"></ul>
                </div>
              </div>
            </article>

            <article class="tank-entry tank-entry--collapsed tank-entry--b" data-entry="b-2">
              <button class="tank-entry__header" id="stream-toggle-b-2" type="button">
                <span class="tank-entry__left"><span class="tank-entry__dot"></span><span class="tank-entry__name" id="tank-b2-name">SLOT-02</span></span>
                <span class="tank-entry__right"><span class="tank-entry__hp" id="tank-b2-hp">--/--</span><span class="tank-entry__chevron" id="tank-b2-chevron">›</span></span>
              </button>
              <div class="tank-entry__detail" id="tank-b2-detail" hidden>
                <div class="hp-bar"><div class="hp-bar-fill" id="tank-b2-hp-bar" style="width: 100%"></div></div>
                <div class="kda-status-row">
                  <div class="kda">
                    <span class="kda-item"><b class="kda-value" id="tank-b2-k">0</b><span class="kda-hint">K</span></span>
                    <span class="kda-item"><b class="kda-value" id="tank-b2-d">0</b><span class="kda-hint">D</span></span>
                  </div>
                  <div class="card-info">
                    <span class="status-badge status-idle" id="tank-b2-status-badge">
                      <span class="status-glyph" aria-hidden="true">●</span>
                      <span class="status-label" id="tank-b2-status-label">待命</span>
                    </span>
                  </div>
                </div>
                <div class="tank-stat-row">
                  <span class="tank-stat-pill" id="tank-b2-hit-pct">HIT 0%</span>
                  <span class="tank-stat-item"><span class="tank-stat-label">MOV</span><span class="tank-stat-val" id="tank-b2-mov">0</span></span>
                  <span class="tank-stat-item"><span class="tank-stat-label">SHO</span><span class="tank-stat-val" id="tank-b2-sho">0</span></span>
                  <span class="tank-stat-item"><span class="tank-stat-label">DMG</span><span class="tank-stat-val" id="tank-b2-dmg">0</span></span>
                </div>
                <div class="tank-pos-row" id="tank-b2-pos-row">POS (00,00)</div>
                <div class="tank-entry__log">
                  <ul class="stream-list" id="tank-b2-stream-list"></ul>
                </div>
              </div>
            </article>

            <article class="tank-entry tank-entry--collapsed tank-entry--b" data-entry="b-3">
              <button class="tank-entry__header" id="stream-toggle-b-3" type="button">
                <span class="tank-entry__left"><span class="tank-entry__dot"></span><span class="tank-entry__name" id="tank-b3-name">SLOT-03</span></span>
                <span class="tank-entry__right"><span class="tank-entry__hp" id="tank-b3-hp">--/--</span><span class="tank-entry__chevron" id="tank-b3-chevron">›</span></span>
              </button>
              <div class="tank-entry__detail" id="tank-b3-detail" hidden>
                <div class="hp-bar"><div class="hp-bar-fill" id="tank-b3-hp-bar" style="width: 100%"></div></div>
                <div class="kda-status-row">
                  <div class="kda">
                    <span class="kda-item"><b class="kda-value" id="tank-b2-k">0</b><span class="kda-hint">K</span></span>
                    <span class="kda-item"><b class="kda-value" id="tank-b2-d">0</b><span class="kda-hint">D</span></span>
                  </div>
                  <div class="card-info">
                    <span class="status-badge status-idle" id="tank-b3-status-badge">
                      <span class="status-glyph" aria-hidden="true">●</span>
                      <span class="status-label" id="tank-b3-status-label">待命</span>
                    </span>
                  </div>
                </div>
                <div class="tank-stat-row">
                  <span class="tank-stat-pill" id="tank-b3-hit-pct">HIT 0%</span>
                  <span class="tank-stat-item"><span class="tank-stat-label">MOV</span><span class="tank-stat-val" id="tank-b3-mov">0</span></span>
                  <span class="tank-stat-item"><span class="tank-stat-label">SHO</span><span class="tank-stat-val" id="tank-b3-sho">0</span></span>
                  <span class="tank-stat-item"><span class="tank-stat-label">DMG</span><span class="tank-stat-val" id="tank-b3-dmg">0</span></span>
                </div>
                <div class="tank-pos-row" id="tank-b3-pos-row">POS (00,00)</div>
                <div class="tank-entry__log">
                  <ul class="stream-list" id="tank-b3-stream-list"></ul>
                </div>
              </div>
            </article>
          </section>
        </div>
      </div>
    </div>
  `;

  // ===== SplitPanel 初始化 (3 条可拖拽 grip) =====
  const splitPanels: SplitPanel[] = [];

  // Grip 1: Sidebar Alpha ↔ Center (horizontal)
  const gripLeftEl = root.querySelector("#grip-left") as HTMLElement;
  const sidebarAlphaEl = root.querySelector("#sidebar-alpha") as HTMLElement;
  const centerColEl = root.querySelector("#center-col") as HTMLElement;
  if (gripLeftEl && sidebarAlphaEl && centerColEl) {
    splitPanels.push(new SplitPanel({
      container: root.querySelector("#battle-main")!,
      panelA: sidebarAlphaEl,
      panelB: centerColEl,
      grip: gripLeftEl,
      direction: "horizontal",
      minSizes: [180, 400],
      defaultSizes: [240, 0],
      onResize: () => {
        resizeCanvas();
      },
    }));
  }

  // Grip 2: Center ↔ Sidebar Bravo (horizontal)
  const gripRightEl = root.querySelector("#grip-right") as HTMLElement;
  const sidebarBravoEl = root.querySelector("#sidebar-bravo") as HTMLElement;
  if (gripRightEl && sidebarBravoEl && centerColEl) {
    splitPanels.push(new SplitPanel({
      container: root.querySelector("#battle-main")!,
      panelA: centerColEl,
      panelB: sidebarBravoEl,
      grip: gripRightEl,
      direction: "horizontal",
      minSizes: [400, 180],
      defaultSizes: [0, 240],
      onResize: () => {
        resizeCanvas();
      },
    }));
  }


  // Grip 3: Battlefield ↔ Console (vertical)
  // FR-026(2026-07-08): Console 默认折叠(36px = 只剩 header), 折叠按钮可切换
  //   折叠状态: 36px, 仅显示 CONSOLE 标题 + tabs + 计数(内部继续记录事件)
  //   展开状态: 230px(原值), 显示 log 列表
  const CONSOLE_COLLAPSED_HEIGHT = 36;
  const CONSOLE_EXPANDED_HEIGHT = 230;
  let consoleCollapsed = true; // 默认折叠
  let consoleSplitPanel: SplitPanel | null = null;
  const gripConsoleEl = root.querySelector("#grip-console") as HTMLElement;
  const battlefieldEl = root.querySelector("#battlefield") as HTMLElement;
  const consoleAreaEl = root.querySelector("#console-area") as HTMLElement;
  if (gripConsoleEl && battlefieldEl && consoleAreaEl) {
    const sp = new SplitPanel({
      container: centerColEl,
      panelA: battlefieldEl,
      panelB: consoleAreaEl,
      grip: gripConsoleEl,
      direction: "vertical",
      minSizes: [200, CONSOLE_COLLAPSED_HEIGHT],
      defaultSizes: [0, CONSOLE_COLLAPSED_HEIGHT], // 默认折叠
      onResize: () => {
        // Canvas 自适应：battlefield 容器 resize 后，canvas 保持居中并按比例缩放
        resizeCanvas();
      },
    });
    splitPanels.push(sp);
    consoleSplitPanel = sp;
  }

  // ===== Console 折叠/展开切换 =====
  const consoleToggleEl = root.querySelector("#console-toggle") as HTMLButtonElement | null;
  if (consoleToggleEl && consoleAreaEl) {
    // 初始折叠状态(DOM class + 按钮已设置为 ▴)
    consoleAreaEl.classList.add("s4-console--collapsed");
    consoleToggleEl.addEventListener("click", () => {
      consoleCollapsed = !consoleCollapsed;
      if (consoleCollapsed) {
        consoleSplitPanel?.setSizes([0, CONSOLE_COLLAPSED_HEIGHT]);
        consoleToggleEl.textContent = "▴";
        consoleToggleEl.title = t("battle.expand_console");
        consoleToggleEl.setAttribute("aria-expanded", "false");
        consoleAreaEl.classList.add("s4-console--collapsed");
      } else {
        consoleSplitPanel?.setSizes([0, CONSOLE_EXPANDED_HEIGHT]);
        consoleToggleEl.textContent = "▾";
        consoleToggleEl.title = t("battle.collapse_console");
        consoleToggleEl.setAttribute("aria-expanded", "true");
        consoleAreaEl.classList.remove("s4-console--collapsed");
      }
      // resizeCanvas 由 setSizes → onResize 回调自动触发
    });
  }

  // ===== Canvas 渲染器 =====
  const canvas = root.querySelector("#battle-canvas") as HTMLCanvasElement;
  // FR-026(2026-07-08): canvas 内部分辨率动态适应 map_size, 不再 hardcode 600×600
  //   cellSize=32 时 25×25 map → canvas.width=800(原生 GDD §12.2 锁值)
  //   未来异形地图(非 25×25)自动适配
  canvas.width = initialState.map_size.width * CANVAS_CELL;
  canvas.height = initialState.map_size.height * CANVAS_CELL;
  const renderer = new CanvasRenderer(canvas, { cellSize: CANVAS_CELL, showGrid: true });
  renderer.loadMap(tiles);
  // FR-022 P2:把 cover positions 传给 renderer,drawCallouts 在 24 个 cover 位置画 supply 标签
  renderer.setCoverPositions(coverPositions);
  // V15(2026-07-28):把完整出生点列表传给 renderer,1v1 也画 2 个 spawn 区域
  renderer.setSpawnPositions({
    team_a: battleMap.spawns?.team_a ?? [mapSpawnA],
    team_b: battleMap.spawns?.team_b ?? [mapSpawnB],
  });

  // FR-029: 监听主题切换，刷新 canvas token
  onThemeChange(() => {
    renderer.refreshTokens();
  });

  // FR-021 改造(2026-07-05): 预加载战场 SVG(异步 fire-and-forget)
  //   - 首帧时 SVG 未就绪会 fallback 到原 hardcode
  //   - 第二帧(30Hz = 33ms)一般已就绪，锐角方块 + HP 3 档生效
  // FR-026(2026-07-08) 修复: SVG preload 完成后立即 re-render initialState
  //   - 之前 fire-and-forget, 5s READY 期间 canvas 显示 hardcode 版本
  //   - 5s READY 结束后第一 tick SVG 已就绪, 切到 SVG 版本 → 视觉跳变("砰的一下切换地图")
  //   - 现在 preload 完成立即 re-render, 5s READY 期间 canvas 切到 SVG 版本, 全程无跳变
  //   - 代价: SVG preload 100-300ms 内 canvas 仍是 hardcode 版本, 但用户视线在 READY 大字上, 不会注意到
  svgLoader.preload(BATTLE_SVG_KEYS).then(() => {
    console.log(`[screen-4] SVG preload done: ${svgLoader.size()}/${BATTLE_SVG_KEYS.length}`);
    // 立即重新渲染 initialState, 让 5s READY 期间 canvas 切到 SVG 版本(避免战斗开始时视觉跳变)
    renderer.render(initialState);
  }).catch((e) => {
    console.warn("[screen-4] SVG preload failed:", e);
  });

  renderer.render(initialState);

  // ===== Canvas 自适应缩放 =====
  // 当 battlefield 容器 resize（通过 SplitPanel grip 拖拽）时，canvas 保持居中并按比例缩放
  function resizeCanvas() {
    const container = battlefieldEl;
    if (!container || !canvas) return;
    const cw = container.clientWidth;
    const ch = container.clientHeight;
    const mapW = currentState.map_size.width * CANVAS_CELL;  // 25×24 = 600
    const mapH = currentState.map_size.height * CANVAS_CELL; // 25×24 = 600
    if (cw === 0 || ch === 0) return;
    // 保持 aspect ratio，fit 容器
    const scale = Math.min(cw / mapW, ch / mapH, 1); // 不超过原生分辨率
    const displayW = Math.floor(mapW * scale);
    const displayH = Math.floor(mapH * scale);
    canvas.style.width = `${displayW}px`;
    canvas.style.height = `${displayH}px`;
  }

  // 初始调用一次
  resizeCanvas();

  // 监听 window resize（浏览器窗口变化也触发自适应）
  window.addEventListener("resize", resizeCanvas);

  // ===== P0 hover info:战场格子信息 tooltip =====
  // 鼠标 hover 在 canvas 任一格子上,实时显示该格元素的信息
  // 优先级:坦克 > spawn marker > cover/obstacle/empty/unreachable
  // 元素类型决定 tooltip 左侧色边(队 A 青 / 队 B 红 / 掩体琥珀 / 障碍警告)
  interface CellTooltipRow { key: string; val: string; valCls?: "good" | "bad" | "warn" }
  interface CellInfo {
    type: "tank-a" | "tank-b" | "cover" | "obstacle" | "spawn-a" | "spawn-b" | "unreachable" | "empty";
    title: string;
    rows: CellTooltipRow[];
    tag?: string;
  }
  /** BotAction → 人类可读标签(坦克行专用) */
  function actionLabel(action: BotAction | undefined): string {
    if (!action || action.type === "idle") return t("battle.action_idle");
    switch (action.type) {
      case "move":   return `${t("battle.action_move_to")}(${action.target.x}, ${action.target.y})`;
      case "shoot":  return t("battle.action_shoot");
      case "scan":   return t("battle.action_scan");
      default:       return t("battle.action_idle");
    }
  }
  /** 反算 (x, y) 上的元素信息,任意格都有(empty 也算) — V3: 多坦克遍历 */
  function getCellInfo(gx: number, gy: number, st: BattleState): CellInfo {
    // V3: 检查 team_a 所有坦克
    if (st.team_a?.tanks) {
      for (let i = 0; i < st.team_a.tanks.length; i++) {
        const tank = st.team_a.tanks[i];
        if (tank.is_alive && tank.position.x === gx && tank.position.y === gy) {
          const hp = tank.health;
          const max = tank.max_health;
          const hpCls: "good" | "warn" | "bad" = hp < max * 0.33 ? "bad" : hp < max * 0.66 ? "warn" : "good";
          const action = currentPlayerActions[i] ?? currentPlayerAction;
          const label = i === 0 ? "TANK_00" : `TANK_0${i}`;
          return {
            type: "tank-a",
            title: `${t("tooltip.you_player")} · ${label}`,
            rows: [
              { key: t("tooltip.team_a_you"), val: "" },
              { key: t("tooltip.position"), val: `(${gx}, ${gy})` },
              { key: t("tooltip.hp"), val: `${Math.max(0, hp)} / ${max}`, valCls: hpCls },
              { key: t("tooltip.cooldown"), val: tank.cooldown_left > 0 ? `${tank.cooldown_left} tick` : t("battle.cooldown_ready") },
              { key: t("tooltip.action"), val: actionLabel(action) },
            ],
          };
        }
      }
    } else {
      // 1v1 fallback
      if (st.self.is_alive && st.self.position.x === gx && st.self.position.y === gy) {
        const hp = st.self.health;
        const max = st.self.max_health;
        const hpCls: "good" | "warn" | "bad" = hp < max * 0.33 ? "bad" : hp < max * 0.66 ? "warn" : "good";
        return {
          type: "tank-a",
          title: t("tooltip.you_player"),
          rows: [
            { key: t("tooltip.team_a_you"), val: "" },
            { key: t("tooltip.position"), val: `(${gx}, ${gy})` },
            { key: t("tooltip.hp"), val: `${Math.max(0, hp)} / ${max}`, valCls: hpCls },
            { key: t("tooltip.cooldown"), val: st.self.cooldown_left > 0 ? `${st.self.cooldown_left} tick` : t("battle.cooldown_ready") },
            { key: t("tooltip.action"), val: actionLabel(currentPlayerAction) },
          ],
        };
      }
    }
    // V3: 检查 team_b 所有坦克
    if (st.team_b?.tanks) {
      for (let i = 0; i < st.team_b.tanks.length; i++) {
        const tank = st.team_b.tanks[i];
        if (tank.is_alive && tank.position.x === gx && tank.position.y === gy) {
          const hp = tank.health;
          const max = tank.max_health;
          const hpCls: "good" | "warn" | "bad" = hp < max * 0.33 ? "bad" : hp < max * 0.66 ? "warn" : "good";
          const label = i === 0 ? "TANK_00" : `TANK_0${i}`;
          return {
            type: "tank-b",
            title: `${t("tooltip.enemy_ai")} · ${label}`,
            rows: [
              { key: t("tooltip.team_b_ai"), val: "" },
              { key: t("tooltip.position"), val: `(${gx}, ${gy})` },
              { key: t("tooltip.hp"), val: `${Math.max(0, hp)} / ${max}`, valCls: hpCls },
              { key: t("tooltip.range"), val: `${TANK_STATS.range} ${t("tooltip.range_chebyshev")}` },
            ],
          };
        }
      }
    } else {
      // 1v1 fallback
      if (st.enemy.is_alive && st.enemy.position.x === gx && st.enemy.position.y === gy) {
        const hp = st.enemy.health;
        const max = st.enemy.max_health;
        const hpCls: "good" | "warn" | "bad" = hp < max * 0.33 ? "bad" : hp < max * 0.66 ? "warn" : "good";
        return {
          type: "tank-b",
          title: t("tooltip.enemy_ai"),
          rows: [
            { key: t("tooltip.team_b_ai"), val: "" },
            { key: t("tooltip.position"), val: `(${gx}, ${gy})` },
            { key: t("tooltip.hp"), val: `${Math.max(0, hp)} / ${max}`, valCls: hpCls },
            { key: t("tooltip.range"), val: `${TANK_STATS.range} ${t("tooltip.range_chebyshev")}` },
          ],
        };
      }
    }
    // 3. spawn marker(4 个出生点)
    if (allTilePositions.spawn.some(([sx, sy]) => sx === gx && sy === gy)) {
      const isA = gx === 0;
      return {
        type: isA ? "spawn-a" : "spawn-b",
        title: t("tooltip.spawn"),
        rows: [
          { key: "", val: isA ? t("tooltip.team_a_player") : t("tooltip.team_b_ai_short") },
          { key: t("tooltip.position"), val: `(${gx}, ${gy})` },
          { key: t("tooltip.enter"), val: t("tooltip.at_start") },
        ],
      };
    }
    // 4. cover(也是 supply zone,callout-supply 标签画在 cover 上)
    if (allTilePositions.cover.some(([cx, cy]) => cx === gx && cy === gy)) {
      return {
        type: "cover",
        title: t("tooltip.cover"),
        rows: [
          { key: t("tooltip.position"), val: `(${gx}, ${gy})` },
          { key: t("tooltip.blocks_move"), val: t("tooltip.no") },
          { key: t("tooltip.blocks_vision"), val: t("tooltip.no") },
          { key: t("tooltip.damage_reduction"), val: "50%", valCls: "good" },
        ],
        tag: t("tooltip.supply_zone"),
      };
    }
    // 5. obstacle
    if (allTilePositions.obstacle.some(([ox, oy]) => ox === gx && oy === gy)) {
      return {
        type: "obstacle",
        title: t("tooltip.obstacle"),
        rows: [
          { key: t("tooltip.position"), val: `(${gx}, ${gy})` },
          { key: t("tooltip.blocks_move"), val: t("tooltip.yes"), valCls: "bad" },
          { key: t("tooltip.blocks_vision"), val: t("tooltip.yes"), valCls: "bad" },
        ],
      };
    }
    // 6. unreachable
    if (allTilePositions.unreachable.some(([ux, uy]) => ux === gx && uy === gy)) {
      return {
        type: "unreachable",
        title: t("tooltip.unreachable"),
        rows: [
          { key: t("tooltip.position"), val: `(${gx}, ${gy})` },
          { key: t("tooltip.enter"), val: t("tooltip.no"), valCls: "bad" },
        ],
      };
    }
    // 7. empty
    return {
      type: "empty",
      title: t("tooltip.empty"),
      rows: [
        { key: t("tooltip.position"), val: `(${gx}, ${gy})` },
        { key: t("tooltip.blocks"), val: t("tooltip.none") },
      ],
    };
  }

  // tooltip DOM(挂在 body 上,position:fixed,避免影响 grid 布局)
  const tooltipEl = document.createElement("div");
  tooltipEl.className = "cell-tooltip";
  tooltipEl.innerHTML = `
    <div class="cell-tooltip-title"></div>
    <div class="cell-tooltip-rows"></div>
  `;
  document.body.appendChild(tooltipEl);
  const tooltipTitleEl = tooltipEl.querySelector(".cell-tooltip-title") as HTMLElement;
  const tooltipRowsEl = tooltipEl.querySelector(".cell-tooltip-rows") as HTMLElement;

  function showTooltip(info: CellInfo, mouseX: number, mouseY: number) {
    tooltipEl.className = `cell-tooltip cell-tooltip--${info.type} visible`;
    tooltipTitleEl.textContent = info.title;
    const rowsHtml = info.rows
      .map(
        (r) =>
          `<div class="cell-tooltip-row"><span class="cell-tooltip-key">${escapeHtml(r.key)}</span><span class="cell-tooltip-val${r.valCls ? ` cell-tooltip-val--${r.valCls}` : ""}">${escapeHtml(r.val)}</span></div>`,
      )
      .join("");
    const tagHtml = info.tag ? `<span class="cell-tooltip-tag">${escapeHtml(info.tag)}</span>` : "";
    tooltipRowsEl.innerHTML = rowsHtml + tagHtml;
    // 定位:鼠标右下偏移;溢出 viewport 时翻向
    const offset = 14;
    const tRect = tooltipEl.getBoundingClientRect();
    let x = mouseX + offset;
    let y = mouseY + offset;
    if (x + tRect.width > window.innerWidth) x = mouseX - tRect.width - offset;
    if (y + tRect.height > window.innerHeight) y = mouseY - tRect.height - offset;
    tooltipEl.style.left = `${Math.max(4, x)}px`;
    tooltipEl.style.top = `${Math.max(4, y)}px`;
  }
  function hideTooltip() {
    tooltipEl.classList.remove("visible");
  }

  // canvas 鼠标事件:把视口坐标反算回 grid 坐标(考虑 canvas display 缩放)
  // P1:CANVAS_CELL 已上移至文件顶部 (L44),避免 resizeCanvas() TDZ
  // 记录最近一次 hover 坐标 + 鼠标视口位置:tick 刷新时也更新 tooltip(坦克移走/HP 变化实时反映)
  let lastHoverCell: { x: number; y: number } | null = null;
  let lastMousePos: { x: number; y: number } | null = null;
  canvas.addEventListener("mousemove", (e) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const px = (e.clientX - rect.left) * scaleX;
    const py = (e.clientY - rect.top) * scaleY;
    const gx = Math.floor(px / CANVAS_CELL);
    const gy = Math.floor(py / CANVAS_CELL);
    const mapW = currentState.map_size.width;
    const mapH = currentState.map_size.height;
    if (gx < 0 || gx >= mapW || gy < 0 || gy >= mapH) {
      hideTooltip();
      lastHoverCell = null;
      lastMousePos = null;
      return;
    }
    lastHoverCell = { x: gx, y: gy };
    lastMousePos = { x: e.clientX, y: e.clientY };
    const info = getCellInfo(gx, gy, currentState);
    showTooltip(info, e.clientX, e.clientY);
  });
  canvas.addEventListener("mouseleave", () => {
    hideTooltip();
    lastHoverCell = null;
    lastMousePos = null;
  });
  // 点击战场上的坦克 → 选中该机器人(高亮对应阵营 stream 头部)
  // demo 1v1:每队 1 个机器人,选中即高亮该队 stream 标题
  // FR-024 P1-2:reflow 触发(.stream-title.selected underline + 0.15s 脉冲)— 重复点击也播动画
  canvas.addEventListener("click", (e) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const px = (e.clientX - rect.left) * scaleX;
    const py = (e.clientY - rect.top) * scaleY;
    const gx = Math.floor(px / CANVAS_CELL);
    const gy = Math.floor(py / CANVAS_CELL);
    const st = currentState;
    // V3: 遍历 team_a/team_b 所有坦克
    const selectTeam = (team: "a" | "b") => {
      const titleEl = team === "a" ? streamTitleAEl : streamTitleBEl;
      titleEl.classList.remove("selected");
      void titleEl.offsetWidth;
      titleEl.classList.add("selected");
    };
    // 优先多坦克检测
    if (st.team_a?.tanks) {
      for (const tank of st.team_a.tanks) {
        if (tank.is_alive && tank.position.x === gx && tank.position.y === gy) {
          selectTeam("a");
          return;
        }
      }
    } else if (st.self.is_alive && st.self.position.x === gx && st.self.position.y === gy) {
      selectTeam("a");
    }
    if (st.team_b?.tanks) {
      for (const tank of st.team_b.tanks) {
        if (tank.is_alive && tank.position.x === gx && tank.position.y === gy) {
          selectTeam("b");
          return;
        }
      }
    } else if (st.enemy.is_alive && st.enemy.position.x === gx && st.enemy.position.y === gy) {
      selectTeam("b");
    }
  });
  /** tick 驱动刷新:如果 tooltip 还在显示,根据当前 state 重算并贴回原鼠标位置 */
  function refreshTooltipIfVisible() {
    if (!lastHoverCell || !lastMousePos) return;
    if (!tooltipEl.classList.contains("visible")) return;
    const info = getCellInfo(lastHoverCell.x, lastHoverCell.y, currentState);
    showTooltip(info, lastMousePos.x, lastMousePos.y);
  }

  // P1:currentState 已上移至 L141 之后,避免 resizeCanvas() TDZ 错误
  let currentPlayerAction: BotAction | undefined;
  let currentAIAction: BotAction | undefined;
  // V3: 多坦克 actions 数组（每个 tank 一个 action）
  let currentPlayerActions: BotAction[] = [];
  let currentAIActions: BotAction[] = [];
  // 简化访问 map：syncMultiTankUI 内部用
  const currentActions: { a: BotAction[]; b: BotAction[] } = { a: [], b: [] };
  let playerKills = 0;
  let playerDeaths = 0;
  // P1-#2:GDD §6.4 屏 5 中文文案需要"发射 N 发子弹""在第 X.Xs 被击杀"——追踪发射次数与阵亡 tick
  let playerShotsFired = 0;
  let playerDeathTick: number | null = null;
  // P1: 动作统计计数器
  let playerMov = 0, playerSho = 0, playerHit = 0, playerDmg = 0;
  let aiMov = 0, aiSho = 0, aiHit = 0, aiDmg = 0;
  let speed: Speed = "1x";
  // V14: 独立渲染循环 — 逻辑 tick 10Hz + rAF 渲染 ~60fps
  let renderLoopId: number | null = null;
  let renderSnapshotState: BattleState = initialState;
  let renderSnapshotActionA: BotAction | undefined;
  let renderSnapshotActionB: BotAction | undefined;
  let lastTickWallTime = performance.now(); // V14: render loop 插值用（后续 task 启用）
  let vfxEventsThisTick: VfxFrameEvents = { damage: [], shoots: [], kills: [] }; // V14: 每 tick 累积的 VFX 事件
  // 拆流:左 stream-a(team_a) / 右 stream-b(team_b),各 8 条上限
  let streamA: StreamEntry[] = [];
  let streamB: StreamEntry[] = [];
  // V3: per-tank 流式日志（streamA2/A3/B2/B3 对应 TANK_02/TANK_03）
  const streamA2: StreamEntry[] = [];
  const streamA3: StreamEntry[] = [];
  const streamB2: StreamEntry[] = [];
  const streamB3: StreamEntry[] = [];
  // V3: team → tank_index → { ulEl, entries } 查找表，让 appendStreamEntry 零解析直达目标 DOM
  const TANK_STREAMS: Record<string, Array<{ ulEl: HTMLElement; entries: StreamEntry[] }>> = {
    a: [],
    b: [],
  };
  // F4: lastActionKey 提升到闭包作用域(之前在 tick handler 内部,每 tick 重置,导致连续 move 到同一 target 被误判为"重复"而跳过记录)
  const lastActionKey: Record<"a" | "b", string> = { a: "", b: "" };
  const STREAM_MAX = 8;
  let lastTickNum = 0;

  // V15: 战斗 MIDI 3 层动态强度 — baseline → advantage(HP<30%) → climax(最后 10s)
  let midiLayer: 'baseline' | 'advantage' | 'climax' = 'baseline';
  let midiLayerReadyTick = 0; // 切换检测的起始 tick（延迟 3s 避免开局误触发）

  const tickNumEl = root.querySelector("#tick-num")!;
  const statusLiveEl = root.querySelector("#status-live")!;
  // P1-#12:OP-01 编号原由屏 3 generateOpId 写入 sessionStorage(v1.0.9 屏 3 已移除)
  // TODO:OP-01 编号生成逻辑需迁移到屏 2,当前 sessionStorage 读不到会显示默认值
  const opIdDisplayEl = root.querySelector("#op-id-display") as HTMLElement | null;
  if (opIdDisplayEl) {
    const opId = sessionStorage.getItem(`op-${projectId}`);
    if (opId) opIdDisplayEl.textContent = opId;
  }
  // V2: 更新 sidebar OP 编号
  const sidebarOpA = root.querySelector("#sidebar-op-a") as HTMLElement | null;
  const sidebarOpB = root.querySelector("#sidebar-op-b") as HTMLElement | null;
  const opId = sessionStorage.getItem(`op-${projectId}`);
  if (opId) {
    if (sidebarOpA) sidebarOpA.textContent = opId;
    if (sidebarOpB) sidebarOpB.textContent = opId;
  }
  // P0-#8:屏 5 顶状态栏 ERROR 字段 DOM 引用
  const errorStatusEl = root.querySelector("#error-status") as HTMLElement;
  const errorCodeEl = root.querySelector("#error-code")!;
  let errorHighlightTimer: number | null = null;

  /** P0-#8:触发顶栏 ERROR 红高亮 1.5s(GDD §13.7 L1746)→ 灰显常驻 */
  function showErrorStatus(errorCode: string) {
    errorCodeEl.textContent = errorCode;
    errorStatusEl.hidden = false;
    errorStatusEl.classList.add("error-active");
    if (errorHighlightTimer !== null) {
      clearTimeout(errorHighlightTimer);
    }
    errorHighlightTimer = window.setTimeout(() => {
      errorStatusEl.classList.remove("error-active");
      errorHighlightTimer = null;
    }, 1500);
  }
  // P1 HP 条: Ardot 横向渐变条(百分比宽度), 用 style.width 驱动态更新
  const playerHpBarFill = root.querySelector("#player-hp-bar .hp-bar-fill") as HTMLElement;
  const aiHpBarFill = root.querySelector("#ai-hp-bar .hp-bar-fill") as HTMLElement;
  const playerHpNumEl = root.querySelector("#player-hp-num") as HTMLElement;
  const aiHpNumEl = root.querySelector("#ai-hp-num") as HTMLElement;
  const playerKEl = root.querySelector("#player-k")!;
  const playerDEl = root.querySelector("#player-d")!;
  root.querySelector("#ai-k")!;
  root.querySelector("#ai-d")!;
  // P0 修复: 移除了 img status icon，改用 span，直接引用 badge 元素本身
  const playerStatusBadgeEl = root.querySelector("#player-status-badge") as HTMLElement;
  const aiStatusBadgeEl = root.querySelector("#ai-status-badge") as HTMLElement;
  // P1: 动作统计 + POS 行 DOM 引用
  const playerHitPctEl = root.querySelector("#player-hit-pct") as HTMLElement;
  const playerMovEl = root.querySelector("#player-mov") as HTMLElement;
  const playerShoEl = root.querySelector("#player-sho") as HTMLElement;
  const playerDmgEl = root.querySelector("#player-dmg") as HTMLElement;
  const playerPosRowEl = root.querySelector("#player-pos-row") as HTMLElement;
  const aiHitPctEl = root.querySelector("#ai-hit-pct") as HTMLElement;
  const aiMovEl = root.querySelector("#ai-mov") as HTMLElement;
  const aiShoEl = root.querySelector("#ai-sho") as HTMLElement;
  const aiDmgEl = root.querySelector("#ai-dmg") as HTMLElement;
  const aiPosRowEl = root.querySelector("#ai-pos-row") as HTMLElement;
  // P2: FIRST BLOOD 覆盖层
  const firstBloodEl = root.querySelector("#battle-first-blood") as HTMLElement;
  let firstBloodShown = false;
  // V14: 击杀标注
  const killFeedEl = root.querySelector("#battle-kill-feed") as HTMLElement;
  let showKillFeed = true; // V14: VFX 控制标志
  // V14: VFX 特效开关面板
  const vfxCtrlEl = root.querySelector("#vfx-ctrl") as HTMLElement;
  const vfxToggleBtn = root.querySelector("#btn-vfx-toggle") as HTMLElement;
  const vfxPanelEl = root.querySelector("#vfx-panel") as HTMLElement;
  const vfxCheckboxes = root.querySelectorAll<HTMLInputElement>("[data-vfx]");
  // 点击 VFX 按钮切换面板显示
  vfxToggleBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    const isOpen = vfxPanelEl.style.display !== "none";
    vfxPanelEl.style.display = isOpen ? "none" : "block";
    vfxToggleBtn.classList.toggle("s4-vfx__btn--open", !isOpen);
  });
  // 点击面板外部关闭
  document.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    if (!vfxCtrlEl.contains(target)) {
      vfxPanelEl.style.display = "none";
      vfxToggleBtn.classList.remove("s4-vfx__btn--open");
    }
  });
  // checkbox 变更 → 同步到 renderer.opts
  vfxCheckboxes.forEach((cb) => {
    cb.addEventListener("change", () => {
      const key = cb.dataset.vfx as keyof RenderOptions;
      const value = cb.checked;
      if (key === "showKillFeed") {
        showKillFeed = value;
        return;
      }
      renderer.setOption(key, value);
    });
  });

  // 侧栏 TankLog DOM 引用
  const streamListAEl = root.querySelector("#stream-list-a") as HTMLElement;
  const streamListBEl = root.querySelector("#stream-list-b") as HTMLElement;
  const streamTitleAEl = root.querySelector("#stream-title-a") as HTMLElement;
  const streamTitleBEl = root.querySelector("#stream-title-b") as HTMLElement;
  // V3: per-tank stream list DOM 引用（TANK_02/TANK_03 日志面板）
  const streamListA2El = root.querySelector("#tank-a2-stream-list") as HTMLElement;
  const streamListA3El = root.querySelector("#tank-a3-stream-list") as HTMLElement;
  const streamListB2El = root.querySelector("#tank-b2-stream-list") as HTMLElement;
  const streamListB3El = root.querySelector("#tank-b3-stream-list") as HTMLElement;
  // V3: 填充 TANK_STREAMS 查找表（零解析直达目标 DOM）
  TANK_STREAMS.a = [
    { ulEl: streamListAEl, entries: streamA },
    { ulEl: streamListA2El, entries: streamA2 },
    { ulEl: streamListA3El, entries: streamA3 },
  ];
  TANK_STREAMS.b = [
    { ulEl: streamListBEl, entries: streamB },
    { ulEl: streamListB2El, entries: streamB2 },
    { ulEl: streamListB3El, entries: streamB3 },
  ];
  // FR-024:head 栏 PHASE 段徽章 DOM 引用
  const headerPhaseBarEl = root.querySelector("#header-phase-bar") as HTMLElement;

  // ===== 速度按钮 (v2: .s4-spd__btn) =====
  root.querySelectorAll<HTMLElement>(".s4-spd__btn[data-speed]").forEach((el) => {
    el.addEventListener("click", () => {
      const newSpeed = el.dataset.speed as Speed;
      speed = newSpeed;
      root.querySelectorAll(".s4-spd__btn").forEach((b) => b.classList.remove("s4-spd__btn--active"));
      el.classList.add("s4-spd__btn--active");
      applySpeed();
    });
  });

  /**
   * 速度生效：把"每 tick 渲染"周期除以 speed multiplier
   * - pause (mult=0) → engine.pause() + clearInterval 现有 damage tick
   * - 1x (mult=1) → 1000/30 ≈ 33.3ms
   * - 2x (mult=2) → 16.6ms
   * - 4x (mult=4) → 8.3ms（UI 可能掉帧，但战斗推进正常）
   */
  function applySpeed() {
    const mult = SPEED_TO_MULT[speed];
    if (mult === 0) {
      engine.pause();
    } else {
      engine.setSpeedMultiplier(mult);
    }
    // P1-#5:不再维护 damageInterval,所有 UI 同步走 engine "tick" event
  }

  // ===== TANK_LOG 手风琴（每个 entry 独立可展开/折叠） =====
  // V3 多坦克模式:3 个 entry 都能独立展开，展开后形态跟第一个一致
  function wireTankAccordion(team: "a" | "b") {
    const container = root.querySelector(`#stream-${team}`) as HTMLElement | null;
    if (!container) return;

    const allEntries = Array.from(container.querySelectorAll<HTMLElement>(`.tank-entry[data-entry^="${team}-"]`));
    if (allEntries.length === 0) return;

    const applyState = (entry: HTMLElement, expanded: boolean) => {
      entry.classList.toggle("tank-entry--expanded", expanded);
      entry.classList.toggle("tank-entry--collapsed", !expanded);
      const chevronEl = entry.querySelector(".tank-entry__chevron");
      if (chevronEl) chevronEl.textContent = expanded ? "▾" : "›";
      const detailEl = entry.querySelector(".tank-entry__detail") as HTMLElement | null;
      if (detailEl) detailEl.hidden = !expanded;
      const btn = entry.querySelector(".tank-entry__header") as HTMLButtonElement | null;
      if (btn) {
        btn.setAttribute("aria-expanded", String(expanded));
        btn.title = expanded ? t("battle.collapse_detail") : t("battle.expand_detail");
      }
    };

    // 初始化:第 1 个 entry 展开,其他折叠
    allEntries.forEach((entry, idx) => {
      applyState(entry, idx === 0);
    });

    // 绑定所有 entry 的 toggle 按钮 — 每个独立展开/折叠
    allEntries.forEach((entry) => {
      const btn = entry.querySelector(".tank-entry__header") as HTMLButtonElement | null;
      if (!btn) return;
      btn.addEventListener("click", () => {
        const willExpand = entry.classList.contains("tank-entry--collapsed");
        applyState(entry, willExpand);
      });
    });
  }

  wireTankAccordion("a");
  wireTankAccordion("b");

  // ===== V3: 多坦克模式 — 重命名 SLOT 2/3 为 TANK_01/02 =====
  // P1-2026-07-12:accordion 行为已由新的 wireTankAccordion 统一处理（每个 entry 独立可展开）
  // 这里只负责命名 + 头部标题（3v3 时显示 NvN）
  const isMultiTank = unitsPerTeam > 1;
  if (isMultiTank) {
    for (let i = 2; i <= unitsPerTeam; i++) {
      const slotA = root.querySelector(`[data-entry="a-${i}"]`);
      const slotB = root.querySelector(`[data-entry="b-${i}"]`);
      const nameA = slotA?.querySelector(".tank-entry__name");
      const nameB = slotB?.querySelector(".tank-entry__name");
      if (nameA) nameA.textContent = `TANK_0${i - 1}`;
      if (nameB) nameB.textContent = `TANK_0${i - 1}`;
    }
    // 更新 header 标题（3v3 时显示 NvN）
    const headerTitleA = root.querySelector(".sidebar-header--alpha .sidebar-header__title");
    if (headerTitleA) headerTitleA.textContent = `${t("battle.team_a_title")} · ${unitsPerTeam}v${unitsPerTeam}`;
    const headerTitleB = root.querySelector(".sidebar-header--bravo .sidebar-header__title");
    if (headerTitleB) headerTitleB.textContent = `${t("battle.team_b_title")} · ${unitsPerTeam}v${unitsPerTeam}`;
  }

  // ===== Give Up =====
  // P1-#6:用 showConfirmModal 替代 window.confirm(WebView/iOS Safari 上被吞)
  root.querySelector("#btn-give-up")?.addEventListener("click", async () => {
    const ok = await showConfirmModal(t("battle.give_up_title"), t("battle.give_up_body"), t("battle.give_up_ok"), t("battle.give_up_cancel"));
    if (ok) {
      engine.stop();
      // F1: Give Up 写放弃 summary（之前不写,screen-5 读上一局残留值）
      const giveUpSummary = {
        winner: "team_b" as const,
        reason: t("result.player_give_up"),
        totalTicks: lastTickNum,
        playerKills,
        playerDeaths,
        durationMs: performance.now() - battleStartTime,
        keyEvents: keyEvents.slice(0, 5),
        consoleLog: consoleEntries.slice(),
        playerShotsFired,
        playerDeathTick,
        // V3: 多坦克终局 HP（全队汇总为总数字，保持 screen-5 BattleSummary 的 number 契约）
        playerFinalHp: currentState.team_a?.tanks
          ? currentState.team_a.tanks.reduce((sum, t) => sum + t.health, 0)
          : currentState.self.health,
        enemyFinalHp: currentState.team_b?.tanks
          ? currentState.team_b.tanks.reduce((sum, t) => sum + t.health, 0)
          : currentState.enemy.health,
        playerActions: { move: playerMov, shoot: playerSho, hit: playerHit, damage: playerDmg },
        aiActions: { move: aiMov, shoot: aiSho, hit: aiHit, damage: aiDmg },
        // V3: 多坦克终局位置
        playerPositions: currentState.team_a?.tanks
          ? currentState.team_a.tanks.map((t) => ({ x: t.position.x, y: t.position.y }))
          : [{ x: currentState.self.position.x, y: currentState.self.position.y }],
        enemyPositions: currentState.team_b?.tanks
          ? currentState.team_b.tanks.map((t) => ({ x: t.position.x, y: t.position.y }))
          : [{ x: currentState.enemy.position.x, y: currentState.enemy.position.y }],
        mapId: selectedMapId,
        gridSize: battleMap.dimensions?.width ?? 25,
        // 向后兼容
        playerPos: currentState.team_a?.tanks?.[0]
          ? { x: currentState.team_a.tanks[0].position.x, y: currentState.team_a.tanks[0].position.y }
          : { x: currentState.self.position.x, y: currentState.self.position.y },
        enemyPos: currentState.team_b?.tanks?.[0]
          ? { x: currentState.team_b.tanks[0].position.x, y: currentState.team_b.tanks[0].position.y }
          : { x: currentState.enemy.position.x, y: currentState.enemy.position.y },
        coverBlocks: coverPositions.map(([x, y]) => ({ x, y })),
        obstacleBlocks: allTilePositions.obstacle.map(([x, y]) => ({ x, y })),
        spawnA: { x: mapSpawnA.x, y: mapSpawnA.y },
        spawnB: { x: mapSpawnB.x, y: mapSpawnB.y },
      };
      try {
        sessionStorage.setItem(`battle-${projectId}`, JSON.stringify(giveUpSummary));
      } catch (e) {
        console.warn("[screen-4] Give Up summary 写入失败:", e);
        sessionStorage.setItem(`battle-${projectId}`, JSON.stringify({ ...giveUpSummary, replay: undefined }));
      }
      onNavigate(pathFor("result", projectId));
    }
  });

// ===== 引擎 =====
const engine = new EngineScheduler();
let screen4ListenerCleanup: (() => void) | null = null;

function cleanupScreen4(): void {
  if (screen4Disposed) return;
  screen4Disposed = true;
  // V14: 清理部署加载遮罩（give up / 退出时确保不残留）
  hideDeployLoading();
  // V14: 清理 rAF 渲染循环
  if (renderLoopId !== null) {
    cancelAnimationFrame(renderLoopId);
    renderLoopId = null;
  }
  readyTimerIds.splice(0).forEach((id) => window.clearTimeout(id));
  readyResolve?.();
  readyResolve = null;
  engine.stop();
  screen4ListenerCleanup?.();
  screen4ListenerCleanup = null;
  splitPanels.forEach((sp) => sp.destroy());
  window.removeEventListener("resize", resizeCanvas);
  hideTooltip();
  tooltipEl.remove();
  root.querySelector(".ready-overlay")?.remove();
  (root as any)._s4Cleanup = undefined;
}

// P1-#5:删除屏 4 自己的 setInterval(tickDamage) 后,所有 UI 同步走 engine.on("tick")
// battleStartTime 提到这里,供 battle_end handler 算 durationMs
let battleStartTime = performance.now();

// P0-#9:屏 5.5 关键事件流(3-5 条按 tick 排序,GDD §13.2.4)
const keyEvents: Array<{ eventType: string; tickNum: number; actor: "team_a" | "team_b"; description: string }> = [];

  // V15: SFX 节流状态 — 避免高频动作(如 move/scan)每 tick 都播放音效
  const lastSfxMs: Record<string, number> = {};

  screen4ListenerCleanup = engine.on((event: EngineEvent) => {
    if (screen4Disposed || !isActive()) return;
    // V2: loading_progress — Pyodide 加载进度
    if (event.type === "loading_progress") {
      const stage = (event as any).stage ?? "Loading...";
      logToConsole({ tick: 0, tag: "EVENT", team: "—", message: `[LOADING] ${stage}` });
      // V14: 同步更新部署加载遮罩 + READY 覆盖层 subtitle
      updateDeployLoading("init_engine", stage);
      const subtitleNumEl = root.querySelector("#ready-subtitle-num");
      if (subtitleNumEl) subtitleNumEl.textContent = stage;
      return;
    }

    if (event.type === "tick") {
      // P0-#6 修复：同步引擎最新 battleState（深拷贝由引擎层做）
      // 用于 HP 节点切分 PHASE + HP UI 同步（之前 currentState 永远不更新，PHASE 永远 OPENING）
      currentState = event.state;

      // V15: 战斗 MIDI 动态分层 — 根据 HP 阈值 + 倒计时自动切换
      if (midiLayerReadyTick === 0) midiLayerReadyTick = event.tickNum;
      const ticksElapsed = event.tickNum - midiLayerReadyTick;
      if (ticksElapsed > 90) { // 至少 3 秒后才开始检测（避免开局立即触发）
        const HP_LOW = 45; // 30% of max 150

        if (midiLayer === 'baseline') {
          // 检测任意一方的任意坦克 HP < 30% → baseline → advantage
          const getHps = (team?: { tanks: { health: number }[] }) =>
            team?.tanks?.map(t => t.health) ?? [];
          const allHps = [...getHps(currentState.team_a), ...getHps(currentState.team_b)];
          if (allHps.length > 0 && allHps.some(hp => hp < HP_LOW)) {
            audioEngine.switchMidi('battle-advantage');
            midiLayer = 'advantage';
          }
        }

        // V15: 最后 10 秒（tick >= 1500, max 1800）→ advantage → climax (BPM +20%)
        if (midiLayer === 'advantage' && event.tickNum >= 1500) {
          audioEngine.switchMidi('battle-advantage', { speed: 1.2 });
          midiLayer = 'climax';
        }
      }
      currentPlayerAction = event.actions.team_a;
      currentAIAction = event.actions.team_b;
      // V3: 多坦克 actions 数组
      if (event.actionsMulti) {
        currentPlayerActions = event.actionsMulti.team_a;
        currentAIActions = event.actionsMulti.team_b;
        currentActions.a = currentPlayerActions;
        currentActions.b = currentAIActions;
        // 兼容旧版：每 tank 独立 action 数组
        (currentState as any)._renderActionsA = currentPlayerActions;
        (currentState as any)._renderActionsB = currentAIActions;
        (currentState as any)._renderActionsB = event.actionsMulti.team_b;
      }
      lastTickNum = event.tickNum;
      tickNumEl.textContent = String(event.tickNum);
      // v2: 更新 head 栏 TICK pill 数值
      const valTickEl = root.querySelector("#val-tick");
      if (valTickEl) valTickEl.textContent = String(event.tickNum).padStart(4, "0");
      // v2: 更新 head 栏 TIME pill 数值
      const valTimeEl = root.querySelector("#val-time");
      if (valTimeEl) {
        const elapsed = Math.floor((performance.now() - battleStartTime) / 1000);
        const min = String(Math.floor(elapsed / 60)).padStart(2, "0");
        const sec = String(elapsed % 60).padStart(2, "0");
        valTimeEl.textContent = `${min}:${sec}/01:30`;
      }
      // PHASE 仅同步 head 栏
      const phase = computePhase(currentState);
      updateHeaderPhaseBar(phase);
      // 同步 HP UI（真实伤害由引擎在 event 触发前已改写 battleState.health）
      syncHpUI();
      // V3: 多坦克 SLOT 同步（HP bar / POS / status）
      if (unitsPerTeam > 1) syncMultiTankUI();
      // V3: 多坦克模式 — 同步 SLOT 2/3 的 HP
      if (isMultiTank && currentState.team_a && currentState.team_b) {
        syncMultiTankUI();
      }
      // P1-#8:状态图标 M/A/C/D 切换(GDD §13.2.2)
      // FR-018 ADR-018.2:enemy 用 is_alive 字段（之前误用 is_visible）
      updateStatusIcon(playerStatusBadgeEl, currentState.self.is_alive, currentState.self.cooldown_left, currentPlayerAction?.type);
      updateStatusIcon(aiStatusBadgeEl, currentState.enemy.is_alive, 0, currentAIAction?.type);
      // V5 P1: 引导浮层触发器（仅 V5 教学关卡）
      if (project.levelId?.startsWith("v5_")) {
        checkAndShowFeedbackTips(currentState, event, battlefieldEl);
      }
      // FR-018 ADR-P0.5:死亡检测**不再**在 tick handler 中触发
      //   之前双触发:tick handler + hit.killed + battle_end 三个地方都 flashDeath/KILL FEED
      //   现在统一由 battle_end 事件触发(下方)
      //   hit.killed 仍发,但不再触发 flashDeath(避免重复)
      // P1-#1:GDD §6.1.6 think() print → KILL FEED 消费
      // V3: 多坦克模式 — tankLogs 按 tank_index 直连对应 stream-list，零前缀解析
      if (event.actionsMulti) {
        // think 日志：tankLogs.team_a[i] → addStreamEntry("a", entry, i)
        const routeTankLogs = (team: "a" | "b", tankLogs: string[][]) => {
          tankLogs.forEach((logs, i) => {
            for (const log of logs) {
              const text = String(log).slice(0, 30);
              addStreamEntry(team, { tick: event.tickNum, text: `[THINK] ${text}`, type: "think" }, i);
            }
          });
        };
        if (event.tankLogs?.team_a) routeTankLogs("a", event.tankLogs.team_a);
        if (event.tankLogs?.team_b) routeTankLogs("b", event.tankLogs.team_b);

        // 每 tank 的 action 独立进 stream
        event.actionsMulti.team_a.forEach((act, i) => {
          if (act.type === "idle") return;
          addStreamEntry("a", {
            tick: event.tickNum,
            text: actionToConsoleMsg(act, "team_a"),
            type: act.type as any,
          }, i);
        });
        event.actionsMulti.team_b.forEach((act, i) => {
          if (act.type === "idle") return;
          addStreamEntry("b", {
            tick: event.tickNum,
            text: actionToConsoleMsg(act, "team_b"),
            type: act.type as any,
          }, i);
        });
      } else {
        // 1v1 路径（原有逻辑）
        if (event.logs?.team_a && event.logs.team_a.length > 0) {
          const thinkText = String(event.logs.team_a[0]).slice(0, 30);
          addStreamEntry("a", { tick: event.tickNum, text: `[THINK] ${thinkText}`, type: "think" });
        }
        if (event.logs?.team_b && event.logs.team_b.length > 0) {
          const thinkText = String(event.logs.team_b[0]).slice(0, 30);
          addStreamEntry("b", { tick: event.tickNum, text: `[THINK] ${thinkText}`, type: "think" });
        }
      }
      // FR-026(2026-07-08) 修复: 之前 move/shoot/scan 只进 CONSOLE, 不进 stream-a/b
      //   → 玩家一直 move 977 次, tank log 区域却空白(用户截图反馈)
      //   现在 move/shoot/scan 也进 stream, 走 STREAM_COLLAPSE_WINDOW=8 折叠
      //   - shoot/scan: 每次都进(开火/扫描是重要事件)
      //   - move: 只在 target 变化时进, 玩家从 (0,12) 推到 (12,12) 12 tick 写 12 条
      //     避免 977 次 move 折叠成 1 条 "T0–976 × 977 移动 → (, )" 的"大事记"化
      //     玩家到 (12,12) 后一直 self.move((12,12)) 不再记录(不变化)
      //   - idle: 不进(每 tick 都 idle 会刷爆)
      // F4: lastActionKey 已提升到闭包作用域(与 streamA/streamB 同级),此处不再声明
      const writeActionToStream = (team: "a" | "b", action: BotAction | undefined) => {
        if (!action) return;
        if (action.type === "idle") return;
        if (action.type !== "move" && action.type !== "shoot" && action.type !== "scan") return;
        // move 模式: target 变化时才记录(key = target 序列化)
        if (action.type === "move") {
          const key = JSON.stringify((action as { target?: { x: number; y: number } }).target ?? null);
          if (key === lastActionKey[team]) return;
          lastActionKey[team] = key;
        }
        addStreamEntry(team, {
          tick: event.tickNum,
          text: actionToConsoleMsg(action, team === "a" ? "team_a" : "team_b"),
          type: action.type,
        });
      };
      writeActionToStream("a", currentPlayerAction);
      writeActionToStream("b", currentAIAction);
      // CONSOLE 日志：tick 内的动作映射
      if (currentPlayerAction) {
        const tag = actionToConsoleTag(currentPlayerAction.type);
        if (tag) {
          const msg = actionToConsoleMsg(currentPlayerAction, "team_a");
          logToConsole({ tick: event.tickNum, tag, team: "A", message: msg, fileLine: `tank_v1.py:${(event.tickNum % 42) + 1}` });
        }
      }
      if (currentAIAction) {
        const tag = actionToConsoleTag(currentAIAction.type);
        if (tag) {
          const msg = actionToConsoleMsg(currentAIAction, "team_b");
          logToConsole({ tick: event.tickNum, tag, team: "B", message: msg, fileLine: `ai_easy.py:${(event.tickNum % 55) + 1}` });
        }
      }
      // P1-#2:追踪玩家发射子弹数(GDD §6.4 "发射 N 发子弹"需要)
      if (event.actions.team_a?.type === "shoot") {
        playerShotsFired++;
        playerSho++;
        // V15: shoot SFX — 玩家开火（冷却自然限频，无需额外节流）
        audioEngine.playSfx('shoot');
      }
      if (event.actions.team_b?.type === "shoot") {
        aiSho++;
        // V15: AI 开火也播放 shoot SFX（增加战斗紧张感）
        audioEngine.playSfx('shoot');
      }
      if (event.actions.team_a?.type === "move") {
        playerMov++;
        // V15: move SFX — 玩家移动（500ms 节流，避免每 tick 触发）
        const now = performance.now();
        if (!lastSfxMs.move || now - lastSfxMs.move >= 500) {
          lastSfxMs.move = now;
          audioEngine.playSfx('move');
        }
      }
      if (event.actions.team_b?.type === "move") aiMov++;
      // V15: scan SFX — 玩家主动扫描（2s 节流，scan 冷却为 5 ticks ≈ 167ms）
      if (event.actions.team_a?.type === "scan") {
        const now = performance.now();
        if (!lastSfxMs.scan || now - lastSfxMs.scan >= 2000) {
          lastSfxMs.scan = now;
          audioEngine.playSfx('scan');
        }
      }
      // P1: 同步 stat/pos
      syncStatUI();
      syncPosUI();
      // V14: 渲染改由独立 rAF loop 驱动，这里仅更新快照 + 收集 VFX 事件
      renderSnapshotState = currentState;
      renderSnapshotActionA = currentPlayerAction;
      renderSnapshotActionB = currentAIAction;
      lastTickWallTime = performance.now();
      // 将本 tick 累积的 VFX 事件推入渲染器
      if (vfxEventsThisTick && (vfxEventsThisTick.damage.length > 0 || vfxEventsThisTick.shoots.length > 0 || vfxEventsThisTick.kills.length > 0)) {
        renderer.recordVfxEvents(vfxEventsThisTick, event.tickNum);
      }
      vfxEventsThisTick = { damage: [], shoots: [], kills: [] };
      // P0 hover info:tank 移动/HP 变化时,已 hover 的 tooltip 同步刷新(不停鼠标也能看到)
      refreshTooltipIfVisible();
    } else if (event.type === "hit") {
      // T26 真实伤害：每次命中写一条流式信息
      // 拆流:命中事件归"射手"所属阵营(shooter=team_a → 左 stream-a)
      // V3: 用 shooterIndex/targetIndex 区分多坦克
      const tIdxName = event.targetIndex != null && event.targetIndex > 0 ? `TANK_0${event.targetIndex}` : "";
      const targetName = event.target === "team_a"
        ? (tIdxName || "YOU")
        : (tIdxName || "AI");
      addStreamEntry(event.shooter === "team_a" ? "a" : "b", {
        tick: event.tickNum,
        text: `→ ${targetName} -${event.damage} HP${event.killed ? " (KILL)" : ""}`,
        type: "shoot",
      });
      // CONSOLE 日志：命中事件
      {
        const shooterTeam = event.shooter === "team_a" ? "A" : "B";
        const hitMsg = `→ ${targetName} -${event.damage} HP${event.killed ? " KILL" : ""}`;
        const tag = event.killed ? "KILL" : "SHOOT";
        logToConsole({ tick: event.tickNum, tag, team: shooterTeam, message: hitMsg });
      }
      // P2-#3:开火特效 — V3: 用 shooterIndex/targetIndex 取精确位置
      const sIdx = event.shooterIndex ?? 0;
      const tIdx = event.targetIndex ?? 0;
      const targetPos = getTankPosition(currentState, event.target, tIdx)
        ?? (event.target === "team_a" ? currentState.self.position : currentState.enemy.position);
      const shooterPos = getTankPosition(currentState, event.shooter, sIdx)
        ?? (event.shooter === "team_a" ? currentState.self.position : currentState.enemy.position);
      renderer.recordFire(event.tickNum, targetPos, event.shooter);
      // P1: 攻击线（从 shooter 位置到 target 位置）
      renderer.recordShoot(event.tickNum, shooterPos, targetPos, event.shooter);
      // FR-022 P0:命中爆点
      renderer.recordHit(event.tickNum, targetPos, event.target);
      // V14: 收集 VFX 事件（射击 + 命中）
      vfxEventsThisTick.shoots.push({ from: shooterPos, to: targetPos, shooterTeam: event.shooter, tickNum: event.tickNum });
      vfxEventsThisTick.damage.push({ targetPos: targetPos, damage: event.damage, tickNum: event.tickNum });
      if (event.killed) {
        vfxEventsThisTick.kills.push({ pos: targetPos, killedTeam: event.target, tickNum: event.tickNum });
        // V15: destroy SFX — 单位被摧毁
        audioEngine.playSfx('destroy');
      }
      // V15: hit SFX — 子弹命中（每次命中都播放，提供即时打击反馈）
      if (event.damage > 0) {
        audioEngine.playSfx('hit');
      }
      // P1: 统计 hit/dmg
      if (event.shooter === "team_a") { playerHit++; playerDmg += event.damage; }
      else { aiHit++; aiDmg += event.damage; }
      // FR-018 ADR-P0.5:hit.killed 仍 emit(供屏 5 总结),但**不再**触发 flashDeath / 累计 KDA
      if (event.killed) {
        // V14: 击杀标注 — 右侧滑入 [ELIMINATED]
        if (showKillFeed) {
          const killedLabel = event.target === "team_a" ? "PLAYER" : "AI";
          const killEntry = document.createElement("div");
          killEntry.className = "battle-kill-entry";
          killEntry.textContent = `[ELIMINATED] ${killedLabel}`;
          killFeedEl.appendChild(killEntry);
          // 动画结束后移除 DOM 节点
          setTimeout(() => { killEntry.remove(); }, 2500);
        }
        // P2: FIRST BLOOD 覆盖层（首次击杀）
        if (!firstBloodShown) {
          firstBloodShown = true;
          firstBloodEl.style.display = "flex";
          setTimeout(() => { firstBloodEl.style.display = "none"; }, 1800);
        }
        if (event.target === "team_a") {
          playerDeaths++;
          playerDEl.textContent = String(playerDeaths);
        } else {
          playerKills++;
          playerKEl.textContent = String(playerKills);
        }
        if (playerDeathTick === null && event.target === "team_a") {
          playerDeathTick = lastTickNum;
        }
      }
    } else if (event.type === "battle_end") {
      statusLiveEl.textContent = t("battle.done");
      statusLiveEl.classList.remove("status-live");
      statusLiveEl.classList.add("status-done");
      // V15: 战斗结束时播放胜利/失败 BGM（硬切）
      audioEngine.onBattleEnd(event.winner as 'team_a' | 'team_b' | 'draw');
      const winner = event.winner;
      // V3: 多坦克团队存活检测 + death flash
      const isTeamAllDead = (team: "team_a" | "team_b"): boolean => {
        const teamState = currentState[team];
        if (teamState?.tanks) return teamState.tanks.every((t) => !t.is_alive);
        // 1v1 fallback
        const single = team === "team_a" ? currentState.self : currentState.enemy;
        return single.health <= 0;
      };
      const getDeadTanks = (team: "team_a" | "team_b"): number[] => {
        const teamState = currentState[team];
        if (teamState?.tanks) {
          return teamState.tanks.reduce<number[]>((acc, t, i) => (!t.is_alive ? [...acc, i] : acc), []);
        }
        // 1v1 fallback
        const single = team === "team_a" ? currentState.self : currentState.enemy;
        return single.health <= 0 ? [0] : [];
      };

      if (isTeamAllDead("team_a")) {
        for (const _ of getDeadTanks("team_a")) renderer.flashDeath("team_a");
        // AI 击毁 Team A → 归 B 队 stream
        addStreamEntry("b", { tick: lastTickNum, text: t("battle.ai_kill_you"), type: "kill" });
        logToConsole({ tick: lastTickNum, tag: "KILL", team: "B", message: t("battle.ai_kill_you") });
      }
      if (isTeamAllDead("team_b")) {
        for (const _ of getDeadTanks("team_b")) renderer.flashDeath("team_b");
        // Team A 击毁 AI → 归 A 队 stream
        addStreamEntry("a", { tick: lastTickNum, text: t("battle.you_kill_ai"), type: "kill" });
        logToConsole({ tick: lastTickNum, tag: "KILL", team: "A", message: t("battle.you_kill_ai") });
      }
      const summary = {
        winner,
        reason: event.reason,
        totalTicks: lastTickNum,
        playerKills,
        playerDeaths,
        durationMs: performance.now() - battleStartTime,
        // P0-#9:屏 5.5 关键事件流(3-5 条)
        keyEvents: keyEvents.slice(0, 5),
        // 屏 5 复盘所需：保留完整战斗日志（供全量日志 + AI 赛后分析）
        consoleLog: consoleEntries.slice(),
        // P1-#2:屏 5 中文文案需要(GDD §6.4)
        playerShotsFired,
        playerDeathTick,
        // V3: 多坦克终局 HP（全队汇总为总数字，保持 screen-5 BattleSummary 的 number 契约）
        playerFinalHp: currentState.team_a?.tanks
          ? currentState.team_a.tanks.reduce((sum, t) => sum + t.health, 0)
          : currentState.self.health,
        enemyFinalHp: currentState.team_b?.tanks
          ? currentState.team_b.tanks.reduce((sum, t) => sum + t.health, 0)
          : currentState.enemy.health,
        // 结果页六维度评分所需的战术统计
        playerActions: {
          move: playerMov,
          shoot: playerSho,
          hit: playerHit,
          damage: playerDmg,
        },
        aiActions: {
          move: aiMov,
          shoot: aiSho,
          hit: aiHit,
          damage: aiDmg,
        },
        // V3: 多坦克终局位置（全 tank 存数组）
        playerPositions: currentState.team_a?.tanks
          ? currentState.team_a.tanks.map((t) => ({ x: t.position.x, y: t.position.y }))
          : [{ x: currentState.self.position.x, y: currentState.self.position.y }],
        enemyPositions: currentState.team_b?.tanks
          ? currentState.team_b.tanks.map((t) => ({ x: t.position.x, y: t.position.y }))
          : [{ x: currentState.enemy.position.x, y: currentState.enemy.position.y }],
        // v34:屏 5 地图渲染所需的真实战斗数据
        //   之前缺失,屏 5 renderBattleMap 只能用占位数据(中心玩家+右上敌人+3 个假掩体),
        //   跟实际战斗无关。v34 起保存真实 mapId / gridSize / 终局位置 / cover / obstacle / spawn。
        mapId: selectedMapId,
        gridSize: battleMap.dimensions?.width ?? 25,
        // 向后兼容: playerPos/enemyPos 指向 tanks[0]
        playerPos: currentState.team_a?.tanks?.[0]
          ? { x: currentState.team_a.tanks[0].position.x, y: currentState.team_a.tanks[0].position.y }
          : { x: currentState.self.position.x, y: currentState.self.position.y },
        enemyPos: currentState.team_b?.tanks?.[0]
          ? { x: currentState.team_b.tanks[0].position.x, y: currentState.team_b.tanks[0].position.y }
          : { x: currentState.enemy.position.x, y: currentState.enemy.position.y },
        // coverPositions 是 [x, y][] 元组,转 {x, y}[] 便于 JSON 序列化 + 屏 5 直接消费
        coverBlocks: coverPositions.map(([x, y]) => ({ x, y })),
        obstacleBlocks: allTilePositions.obstacle.map(([x, y]) => ({ x, y })),
        spawnA: { x: mapSpawnA.x, y: mapSpawnA.y },
        spawnB: { x: mapSpawnB.x, y: mapSpawnB.y },
      };
      // V17: 首次通关检测 — 胜利时置 flag 供屏 5 显示庆祝提示
      const playerWon = winner === "team_a" || isTeamAllDead("team_b");
      const btState = getBattleTeachingState();
      if (playerWon && btState && btState.hintDensity > 0) {
        try {
          sessionStorage.setItem(`battlecode-first-clear-${projectId}`, "true");
        } catch { /* ignore */ }
      }

      try {
        sessionStorage.setItem(`battle-${projectId}`, JSON.stringify(summary));
      } catch (e) {
        console.warn("[screen-4] sessionStorage summary 写入失败:", e);
        // fallback：去 replay 只存元数据
        sessionStorage.setItem(
          `battle-${projectId}`,
          JSON.stringify({ ...summary, replay: undefined }),
        );
      }
      const resultTimerId = window.setTimeout(() => {
        const index = readyTimerIds.indexOf(resultTimerId);
        if (index >= 0) readyTimerIds.splice(index, 1);
        if (!screen4Disposed && isActive()) onNavigate(pathFor("result", projectId));
      }, 1500);
      readyTimerIds.push(resultTimerId);
    } else if (event.type === "key_event") {
      // P0-#9:屏 5.5 关键事件流(按 tick 排序)
      keyEvents.push({
        eventType: event.eventType,
        tickNum: event.tickNum,
        actor: event.actor,
        description: event.description,
      });
      // FR-026(2026-07-08): key_event 从 stream-a/b 移到 CONSOLE
      //   - 之前 stream 文本 "[★ SCAN_FAILED] AI scan超距(dist=12,vision=10)" 冗长, 自动换行 2 行
      //   - 跟玩家自己动作(move/shoot/scan)的简洁文本风格不一致(用户截图反馈左右 log 风格不对称)
      //   - 现在 key_event 只进 CONSOLE(debug 视图), TANK LOG 只显示 tank 自己动作
      //   - keyEvents 数组仍收集(屏 5.5 用), 不影响
      const team = event.actor === "team_a" ? "A" : "B";
      logToConsole({
        tick: event.tickNum,
        tag: "EVENT",
        team,
        message: `[${event.eventType}] ${event.description}`,
      });
    } else if (event.type === "error") {
      // V2: 区分 error.source — player 错误进 stream-a，AI 错误进 stream-b
      // P0-#8 修复:用 engine 传来的 errorCode 字段(不再随机生成 0x5E10-0x5E69)
      // 顶栏 [ERROR] · 0xXXXX 红高亮 1.5s(GDD §13.7 L1746)→ 灰显常驻
      const errorSource = (event as any).source as string | undefined;
      const isAiError = errorSource === "ai";
      const isPlayerError = errorSource === "player";
      const errorTag = isAiError ? "AI_RUNTIME_ERR" : (isPlayerError ? "RUNTIME_ERR" : "ERROR");
      const errorPrefix = isAiError ? "[AI_RUNTIME_ERR]" : (isPlayerError ? "[RUNTIME_ERR]" : "[ERROR]");
      const errorTeam = isAiError ? "B" : (isPlayerError ? "A" : "—");

      if (isPlayerError) {
        // 玩家错误只进 stream-a
        addStreamEntry("a", { tick: event.tickNum, text: `${errorPrefix} ${event.errorCode} ${event.message}`, type: "error" });
      } else if (isAiError) {
        // AI 错误只进 stream-b
        addStreamEntry("b", { tick: event.tickNum, text: `${errorPrefix} ${event.errorCode} ${event.message}`, type: "error" });
      } else {
        // 系统级错误归双方 stream
        addStreamEntry("a", { tick: event.tickNum, text: `[ERROR ${event.errorCode}] ${event.message}`, type: "error" });
        addStreamEntry("b", { tick: event.tickNum, text: `[ERROR ${event.errorCode}] ${event.message}`, type: "error" });
      }
      logToConsole({ tick: event.tickNum, tag: errorTag, team: errorTeam, message: `${event.errorCode} ${event.message}` });
      showErrorStatus(event.errorCode);
    }
  });

  if (!isActive()) return;

  // 模拟伤害/tick loop（demo 简化：引擎不真算伤害，屏 4 自己维护 HP）
  // 实际项目：引擎会传 damage 事件，这里简化为：每个 tick 双方各扣 1 HP（demo demo）
  // 真实版本应该由引擎计算 damage 并 emit
  // T26 真实伤害修复（2026-07-04 paul-battle-damage）：HP 现在由引擎在 tick 事件内直接改写 battleState
  // P1-#5:tickDamage / damageInterval / startDamageInterval 全部删除
  // 所有 UI 同步 + 死亡检测已合并到 engine.on("tick") handler,不再用屏 4 自己的 setInterval
  // (之前双时钟 setInterval(tickDamage) + setTimeout(engineTick) 互相竞争,HP 跳变 1-2 帧)
  function syncHpUI() {
    // FR-018 P0-#6:用 state.self.max_health 而非 hardcode 150
    // P1:Ardot 横向渐变条,百分比宽度驱动 + HP数字右对齐
    const selfMax = currentState.self.max_health || TANK_STATS.max_health;
    const enemyMax = currentState.enemy.max_health || TANK_STATS.max_health;
    const selfPct = Math.max(0, Math.min(100, Math.round((currentState.self.health / selfMax) * 100)));
    const enemyPct = Math.max(0, Math.min(100, Math.round((currentState.enemy.health / enemyMax) * 100)));
    playerHpBarFill.style.width = selfPct + '%';
    aiHpBarFill.style.width = enemyPct + '%';
    // Ardot 终稿 HP 数字
    playerHpNumEl.textContent = `${Math.max(0, currentState.self.health)}/${selfMax}`;
    aiHpNumEl.textContent = `${Math.max(0, currentState.enemy.health)}/${enemyMax}`;
  }

  /** V3: 多坦克 SLOT 2/3 HP 同步 */
  function syncMultiTankUI() {
    if (!currentState.team_a || !currentState.team_b) return;
    const updateSlot = (team: "a" | "b", tankIdx: number) => {
      const slotNum = tankIdx + 1; // 0→1 (主), 1→2, 2→3
      const teamState = team === "a" ? currentState.team_a! : currentState.team_b!;
      const tank = teamState.tanks[tankIdx];
      if (!tank) return;
      const hp = Math.max(0, tank.health);
      const maxHp = tank.max_health || TANK_STATS.max_health;

      // HP 文本
      const hpEl = root.querySelector(`#tank-${team}${slotNum}-hp`);
      if (hpEl) hpEl.textContent = tank.is_alive ? `${hp}/${maxHp}` : "✖";

      // 阵亡时变灰
      const slotEl = root.querySelector(`[data-entry="${team}-${slotNum}"]`) as HTMLElement;
      if (slotEl) {
        slotEl.style.opacity = tank.is_alive ? "1" : "0.4";
      }

      // HP bar 进度
      const hpBarEl = root.querySelector(`#tank-${team}${slotNum}-hp-bar`) as HTMLElement;
      if (hpBarEl) {
        const pct = Math.max(0, Math.min(100, Math.round((hp / maxHp) * 100)));
        hpBarEl.style.width = pct + "%";
      }

      // POS 行
      const posEl = root.querySelector(`#tank-${team}${slotNum}-pos-row`) as HTMLElement;
      if (posEl) {
        posEl.textContent = `POS (${String(tank.position.x).padStart(2, "0")},${String(tank.position.y).padStart(2, "0")})`;
      }

      // 状态 badge（用对应 tank 的 action + cooldown）
      const action = currentActions[team]?.[tankIdx];
      const statusBadgeEl = root.querySelector(`#tank-${team}${slotNum}-status-badge`) as HTMLElement;
      const statusLabelEl = root.querySelector(`#tank-${team}${slotNum}-status-label`);
      if (statusBadgeEl && statusLabelEl) {
        const { cls, label, glyph } = computeStatus(tank.is_alive, tank.cooldown_left, action?.type);
        statusBadgeEl.className = `status-badge ${cls}`;
        const glyphEl = statusBadgeEl.querySelector(".status-glyph");
        if (glyphEl) glyphEl.textContent = glyph;
        statusLabelEl.textContent = label;
      }
    };
    for (let i = 0; i < unitsPerTeam; i++) {
      updateSlot("a", i);
      updateSlot("b", i);
    }
  }

  /** 工具：计算 tank 状态 badge（M/A/C/D） */
  function computeStatus(isAlive: boolean, cooldownLeft: number, actionType: string | undefined): { cls: string; label: string; glyph: string } {
    if (!isAlive) return { cls: "status-dead", label: "阵亡", glyph: "✖" };
    if (cooldownLeft > 0) return { cls: "status-cooldown", label: "冷却", glyph: "◌" };
    if (actionType === "shoot") return { cls: "status-aiming", label: "瞄准", glyph: "◎" };
    if (actionType === "move") return { cls: "status-moving", label: "移动", glyph: "➤" };
    return { cls: "status-idle", label: "待命", glyph: "●" };
  }

  /** P1: 动作统计行更新 */
  function syncStatUI() {
    const pHitPct = playerSho > 0 ? Math.round((playerHit / playerSho) * 100) : 0;
    const aHitPct = aiSho > 0 ? Math.round((aiHit / aiSho) * 100) : 0;
    playerHitPctEl.textContent = `HIT ${pHitPct}%`;
    playerMovEl.textContent = String(playerMov);
    playerShoEl.textContent = String(playerSho);
    playerDmgEl.textContent = String(playerDmg);
    aiHitPctEl.textContent = `HIT ${aHitPct}%`;
    aiMovEl.textContent = String(aiMov);
    aiShoEl.textContent = String(aiSho);
    aiDmgEl.textContent = String(aiDmg);
  }

  /** P1: POS 坐标行更新 */
  function syncPosUI() {
    const sp = currentState.self.position;
    const ep = currentState.enemy.position;
    playerPosRowEl.textContent = `POS (${String(sp.x).padStart(2,"0")},${String(sp.y).padStart(2,"0")})`;
    aiPosRowEl.textContent = `POS (${String(ep.x).padStart(2,"0")},${String(ep.y).padStart(2,"0")})`;
  }

  /**
   * P1-#8 + FR-022 P1:GDD §13.2.2 锁屏 4 玩家卡 5 状态图标
   *   M 移动中 (action.type === "move")        → 移动
   *   A 瞄准中 (action.type === "shoot" + cooldown_left === 0) → 瞄准
   *   C 冷却中 (cooldown_left > 0)             → 冷却
   *   D 已死亡 (is_alive === false)            → 阵亡
   *   idle (其他)                              → 待命
   * 优先级:D > C > A > M > idle
   * FR-022:5 状态切 SVG(FR-020 §2.4-2.8 16×16),通过 el.src 切换 + className 维持颜色状态
   * el 指向 .status-icon(图片),同 .status-badge 容器同步切 class 和 text label
   */
  function updateStatusIcon(badgeEl: HTMLElement, isAlive: boolean, cooldownLeft: number, actionType: string | undefined) {
    let cls: string;
    let tooltip: string;
    let label: string;
    let glyph: string;
    if (!isAlive) {
      cls = "status-dead"; tooltip = t("battle.status_dead"); label = t("battle.status_dead"); glyph = "✖";
    } else if (cooldownLeft > 0) {
      cls = "status-cooldown"; tooltip = t("battle.status_cooldown"); label = t("battle.status_cooldown"); glyph = "◌";
    } else if (actionType === "shoot") {
      cls = "status-aiming"; tooltip = t("battle.status_aiming"); label = t("battle.status_aiming"); glyph = "◎";
    } else if (actionType === "move") {
      cls = "status-moving"; tooltip = t("battle.status_moving"); label = t("battle.status_moving"); glyph = "➤";
    } else {
      cls = "status-idle"; tooltip = t("battle.status_idle"); label = t("battle.status_idle"); glyph = "●";
    }
    // P0 修复: badgeEl 直接是 .status-badge 容器 (不再是 img)
    badgeEl.className = `status-badge ${cls}`;
    badgeEl.title = tooltip;
    const labelEl = badgeEl.querySelector(".status-label");
    if (labelEl) labelEl.textContent = label;
    const glyphEl = badgeEl.querySelector(".status-glyph");
    if (glyphEl) glyphEl.textContent = glyph;
  }

  // P1-#5:删 startDamageInterval + 初始调用(改用 engine.on("tick") 单时钟驱动)

  // ===== 5s 战前 READY 仪式(B3 改造 2026-07-05) =====
  // 屏 4 进入时不立即 start engine,先 5s READY 大字 + 战场缩略 + 顶栏 STANDBY
  // 倒计时归零 → fade out → 战斗开始
  // 顶栏状态: TICK — / PHASE PENDING / ● STANDBY(灰) → TICK 0 / PHASE OPENING / ● LIVE(绿)
  const screen4El = root.querySelector(".s4-root")!;
  const readyOverlay = document.createElement("div");
  readyOverlay.className = "ready-overlay";
  readyOverlay.innerHTML = `
    <div class="ready-content">
      <!-- FR-023(2026-07-05):⚔ → icon-sword SVG(读屏 4 svgLoader,已经 import 过了) -->
      <div class="ready-title">
        <img class="ready-title-icon" src="${svgLoader.getUrl("icon-sword") ?? ""}" alt="" />READY<img class="ready-title-icon" src="${svgLoader.getUrl("icon-sword") ?? ""}" alt="" />
      </div>
      <div class="ready-countdown" id="ready-countdown">3</div>
      <div class="ready-subtitle">${t("battle.ready_title")}<span id="ready-subtitle-num">3</span>s</div>
    </div>
  `;
  screen4El.appendChild(readyOverlay);
  (root as any)._s4Cleanup = cleanupScreen4;

  // 顶栏状态:STANDBY(灰)
  statusLiveEl.textContent = t("battle.standby");
  statusLiveEl.classList.remove("status-live");
  statusLiveEl.classList.add("status-standby");
  tickNumEl.textContent = "—";
  // v2: 同步 head 栏 TICK pill 数值为 "----"
  const valTickEl2 = root.querySelector("#val-tick");
  if (valTickEl2) valTickEl2.textContent = "----";
  // FR-024:PHASE 段徽章在 head 栏,STANDBY 时全部 inactive(opacity 0.3,无 active class)
  // 5s READY 期间不需要更新 head 段徽章,等战斗开始后由 tick handler 驱动
  headerPhaseBarEl.querySelectorAll(".phase-seg").forEach((el) => el.classList.remove("active"));

  // 5s 倒计时
  await new Promise<void>((resolve) => {
    let seconds = 3;
    readyResolve = resolve;
    const countdownEl = readyOverlay.querySelector("#ready-countdown") as HTMLElement;
    const subtitleNumEl = readyOverlay.querySelector("#ready-subtitle-num")!;
    const schedule = (callback: () => void, delay: number) => {
      const timerId = window.setTimeout(() => {
        const index = readyTimerIds.indexOf(timerId);
        if (index >= 0) readyTimerIds.splice(index, 1);
        if (!screen4Disposed) callback();
      }, delay);
      readyTimerIds.push(timerId);
    };
    const tick = () => {
      if (seconds > 0) {
        countdownEl.textContent = String(seconds);
        subtitleNumEl.textContent = String(seconds);
        // 强制 reflow 重启动画(让每个数字都播一次 pulse)
        countdownEl.classList.remove("ready-countdown-pulse");
        void countdownEl.offsetWidth;
        countdownEl.classList.add("ready-countdown-pulse");
        seconds--;
        schedule(tick, 1000);
      } else {
        // 归零:FIGHT! 大字 + 持续 0.5s + fade out 0.3s
        countdownEl.textContent = "FIGHT!";
        countdownEl.classList.add("ready-countdown-fight");
        schedule(() => {
          readyOverlay.classList.add("fade-out");
          schedule(() => {
            readyOverlay.remove();
            // 顶栏状态:LIVE
            statusLiveEl.textContent = t("battle.live");
            statusLiveEl.classList.remove("status-standby");
            statusLiveEl.classList.add("status-live");
            tickNumEl.textContent = "0";
            // v2: 同步 head 栏 TICK pill
            const valTickEl3 = root.querySelector("#val-tick");
            if (valTickEl3) valTickEl3.textContent = "0000";
            // 战斗开始时仅激活 head 栏 OPENING 段
            updateHeaderPhaseBar("OPENING");
            // 重置 battleStartTime:避免 5s READY 时间被算进战斗 durationMs
            battleStartTime = performance.now();
            readyResolve = null;
            resolve();
          }, 300); // fade out 动画时长
        }, 500); // FIGHT! 持续时长
      }
    };
    // 第一秒立即显示 "3",1s 后开始 tick
    countdownEl.classList.add("ready-countdown-pulse");
    schedule(tick, 1000);
  });

  if (!isActive()) return;

  // ===== 阵营流式信息(stream-a / stream-b)=====
  // F1 流:新事件从底部追加,老事件从顶部淡出,上限 STREAM_MAX 条
  // 半透明浮层显示,支持收起/展开(默认展开)
  // FR-024 P0-1:同类事件折叠(同 type + 同 actor + 8 tick 窗口内 → 合并为 "T1310–1315 × 6 文本")
  //   - kill / error / phase 永不折叠(关键事件)
  //   - rAF 异步避免 4x 速度时阻塞 tick 主路径
  function addStreamEntry(team: "a" | "b", entry: StreamEntry, tankIndex = 0) {
    requestAnimationFrame(() => appendStreamEntry(team, entry, tankIndex));
  }
  function appendStreamEntry(team: "a" | "b", entry: StreamEntry, tankIndex = 0) {
    const stream = TANK_STREAMS[team][tankIndex];
    if (!stream) return; // tankIndex 超出范围（如 2v2 时 index=2 不存在）
    const { ulEl, entries: list } = stream;
    list.push(entry);
    if (list.length > STREAM_MAX) {
      list.shift();
      if (ulEl.firstChild) ulEl.removeChild(ulEl.firstChild);
    }
    const li = document.createElement("li");
    li.className = `sf-${entry.type} stream-enter`;
    li.innerHTML = renderStreamLi(entry);
    ulEl.appendChild(li);
  }
  /** 渲染单条 stream li 的 HTML（不折叠，每 tick 独立一行） */
  function renderStreamLi(entry: StreamEntry): string {
    return `<span class="sf-tick">T${entry.tick}</span><span class="sf-text">${escapeHtml(entry.text)}</span>`;
  }
  // 默认选中第一个机器人(YOU / ENEMY):高亮对应 tank-entry 头部名字
  streamTitleAEl.classList.add("selected");
  streamTitleBEl.classList.add("selected");

  // ===== CONSOLE 多标签栏 =====
  // 日志条目池（按类型过滤），与 stream-a/stream-b 独立
  const consoleLogEl = root.querySelector("#console-log") as HTMLElement;
  const consoleEmptyEl = root.querySelector("#console-empty") as HTMLElement;
  const consoleTabsEl = root.querySelector("#console-tabs") as HTMLElement;
  const consoleTabButtons = consoleTabsEl.querySelectorAll<HTMLButtonElement>(".s4-console-tab");
  let consoleFilter: string = "all";
  const consoleEntries: Array<{ tick: number; tag: string; team: string; message: string; fileLine?: string }> = [];
  const CONSOLE_MAX = 200; // 日志上限
  // Ardot 终稿: tab 计数器
  const tabCounts: Record<string, number> = { all: 0, team_a: 0, team_b: 0, kill: 0, error: 0 };

  // F2: 统一过滤比较函数 — pushConsoleEntry 和 refreshConsole 共用,避免两处过滤键不一致
  // team 过滤统一用 "team_" + entry.team.toLowerCase()（与 data-filter 属性值 team_a/team_b 对齐）
  const ERROR_TAGS = ["ERROR", "RUNTIME_ERR", "AI_RUNTIME_ERR"]; // F3: ERR 计数扩展的 tag 列表
  function matchFilter(entry: { tag: string; team: string }, filter: string): boolean {
    if (filter === "all") return true;
    if (filter === "error") return ERROR_TAGS.includes(entry.tag.toUpperCase());
    if (filter === "kill") return entry.tag.toUpperCase() === "KILL";
    // team_a / team_b
    return filter === "team_" + entry.team.toLowerCase();
  }

  // 8 色 tag 背景色板（FR-029: 改用 color-tokens 读取，Day 模式自动适配）
  const TAG_BG: Record<string, string> = {
    KILL:  TOKEN_MAP.red(),
    ERROR: TOKEN_MAP.red(),
    RUNTIME_ERR: TOKEN_MAP.red(),
    AI_RUNTIME_ERR: "#9B59B6",
    PHASE: TOKEN_MAP.cyan(),
    MOVE:  TOKEN_MAP.cyan(),
    SHOOT: TOKEN_MAP.amber(),
    SCAN:  "#3B82F6",
    THINK: "#8B5CF6",
    MISS:  "#EC4899",
    EVENT: TOKEN_MAP.cyan(),
  };

  /** 更新 tab 计数显示 */
  function updateTabCounts() {
    const countAllEl = root.querySelector("#tab-count-all") as HTMLElement | null;
    const countAEl = root.querySelector("#tab-count-team_a") as HTMLElement | null;
    const countBEl = root.querySelector("#tab-count-team_b") as HTMLElement | null;
    const countKillEl = root.querySelector("#tab-count-kill") as HTMLElement | null;
    const countErrEl = root.querySelector("#tab-count-error") as HTMLElement | null;
    const visibleEl = root.querySelector("#console-visible") as HTMLElement | null;
    if (countAllEl) countAllEl.textContent = String(tabCounts.all);
    if (countAEl) countAEl.textContent = String(tabCounts.team_a);
    if (countBEl) countBEl.textContent = String(tabCounts.team_b);
    if (countKillEl) countKillEl.textContent = String(tabCounts.kill);
    if (countErrEl) countErrEl.textContent = String(tabCounts.error);
    // Ardot 终稿: "12/18 visible" — 显示当前过滤数/总数
    if (visibleEl) {
      // F2: 统一用 matchFilter 计算过滤数
      const filtered = consoleEntries.filter(e => matchFilter(e, consoleFilter)).length;
      visibleEl.textContent = `${filtered}/${tabCounts.all} ${t("battle.console_lines")}`;
    }
  }

  /** 推一条日志到 CONSOLE（独立于 stream 流式面板） */
  function pushConsoleEntry(entry: { tick: number; tag: string; team: string; message: string; fileLine?: string }) {
    // V17: hintDensity 密度过滤 — 按教学阶段控制 CONSOLE 信息量
    const teachingState = getBattleTeachingState();
    const density = teachingState?.hintDensity ?? 5;
    const tag = entry.tag.toUpperCase();

    // density 0（考核）: 完全静默
    if (density === 0) return;

    // density 1（1.2.x）: 仅 KILL / ERR / BATTLE_END
    if (density === 1 && !(tag === "KILL" || tag.startsWith("ERR") || tag === "BATTLE_END")) {
      return;
    }

    // density 3（1.1.x）: 过滤掉高频琐碎事件（MOVE/SCAN/THINK）
    if (density === 3 && (tag === "MOVE" || tag === "SCAN" || tag === "THINK" || tag === "TICK" || tag === "LOADING")) {
      return;
    }

    consoleEntries.push(entry);
    if (consoleEntries.length > CONSOLE_MAX) consoleEntries.shift();
    // 更新 tab 计数
    tabCounts.all++;
    const teamKey = "team_" + entry.team.toLowerCase();
    if (teamKey in tabCounts) tabCounts[teamKey]++;
    if (tag === "KILL") tabCounts.kill++;
    // F3: ERR 计数扩展 — ERROR / RUNTIME_ERR / AI_RUNTIME_ERR 都计入
    if (ERROR_TAGS.includes(tag)) tabCounts.error++;
    updateTabCounts();
    // F2: 统一用 matchFilter 过滤
    if (matchFilter(entry, consoleFilter)) {
      appendConsoleLine(entry);
    }
  }

  /** 追加一条日志行到 DOM（Ardot 终稿: 5列 tick/tag/team/msg/file + ERR行高亮） */
  function appendConsoleLine(entry: { tick: number; tag: string; team: string; message: string; fileLine?: string }) {
    consoleEmptyEl.style.display = "none";
    const line = document.createElement("div");
    const tagKey = entry.tag.toUpperCase();
    // F3: isError 扩展为包含 ERROR / RUNTIME_ERR / AI_RUNTIME_ERR
    const isError = ERROR_TAGS.includes(tagKey);
    const isKill = tagKey === "KILL";
    line.className = "s4-console-line" + (isError ? " s4-console-line--error" : "") + (isKill ? " s4-console-line--kill" : "");
    line.dataset.tag = entry.tag.toLowerCase();
    line.dataset.team = entry.team;
    const tickStr = String(entry.tick).padStart(4, "0");
    const tagBg = TAG_BG[tagKey] || TOKEN_MAP.textMuted();
    const fileLineHtml = entry.fileLine ? `<span class="console-fileline">${escapeHtml(entry.fileLine)}</span>` : "";
    const teamColor = entry.team === "A" ? TOKEN_MAP.cyan() : TOKEN_MAP.red();
    line.innerHTML = `
      <span class="console-tick">${tickStr}</span>
      <span class="console-tag" style="background:${tagBg}">${escapeHtml(tagKey)}</span>
      <span class="console-team" style="color:${teamColor}">[${escapeHtml(entry.team)}]</span>
      <span class="console-msg">${escapeHtml(entry.message)}</span>
      ${fileLineHtml}
    `;
    consoleLogEl.appendChild(line);
    // 自动滚到底部
    consoleLogEl.scrollTop = consoleLogEl.scrollHeight;
  }

  /** 按 filter 重建 CONSOLE DOM */
  function refreshConsole() {
    consoleLogEl.innerHTML = "";
    // F2: 统一用 matchFilter 过滤（之前用 e.tag.toLowerCase() 和 e.team 比较,与 data-filter 属性值不一致）
    const filtered = consoleEntries.filter((e) => matchFilter(e, consoleFilter));
    if (filtered.length === 0) {
      consoleEmptyEl.style.display = "";
    } else {
      consoleEmptyEl.style.display = "none";
      for (const entry of filtered) {
        appendConsoleLine(entry);
      }
    }
  }

  // 标签切换
  consoleTabButtons.forEach((btn) => {
    btn.addEventListener("click", () => {
      consoleTabButtons.forEach((b) => b.classList.remove("s4-console-tab--active"));
      btn.classList.add("s4-console-tab--active");
      consoleFilter = btn.dataset.filter || "all";
      refreshConsole();
    });
  });

  // 引擎事件 → CONSOLE 日志映射
  function logToConsole(entry: { tick: number; tag: string; team: string; message: string; fileLine?: string }) {
    pushConsoleEntry(entry);
  }
  // FR-024:head 栏 PHASE 段徽章切换(2026-07-06 stub,完整 PHASE 行为后续 FR-024 接管)
  function updateHeaderPhaseBar(phase: string) {
    headerPhaseBarEl.querySelectorAll(".phase-seg").forEach((el) => el.classList.remove("active"));
    headerPhaseBarEl.querySelector(`[data-phase="${phase}"]`)?.classList.add("active");
  }

  // ===== 启动引擎（放在 renderScreen4Battle 末尾，确保 listener/CONSOLE 状态全部就绪）=====
  try {
    // V3 修复：把 selectedMapId 透传给 engine，否则 loadRules 永远 fallback 到 battle_easy
    // 导致 unitsPerTeam 永远是 1，多坦克路径不会被触发
    engine.setLevelId(selectedMapId);
    // V3: 多坦克模式传 tank_codes 数组，1v1 传 project.code（向后兼容）
    const playerCodes = project.tank_codes && project.tank_codes.length > 1
      ? project.tank_codes : project.code;
    await engine.start(playerCodes, initialState, aiCode);
    // V14: 引擎就绪 → 隐藏部署加载遮罩
    hideDeployLoading();
    // V14: 启动独立 rAF 渲染循环（逻辑 10Hz + 渲染 ~60fps 解耦）
    function renderLoop(): void {
      if (screen4Disposed || !isActive()) { renderLoopId = null; return; }
      const dt = performance.now() - lastTickWallTime; // V14: 插值 delta（后续 task 启用）
      void dt;
      try {
        renderer.render(renderSnapshotState, renderSnapshotActionA, renderSnapshotActionB);
      } catch (e) {
        console.error("[V14 RenderLoop]", e);
      }
      renderLoopId = requestAnimationFrame(renderLoop);
    }
    renderLoopId = requestAnimationFrame(renderLoop);
  } catch (e) {
    const msg = `[FATAL] engine start failed: ${(e as Error).message}`;
    addStreamEntry("a", { tick: 0, text: msg, type: "error" });
    addStreamEntry("b", { tick: 0, text: msg, type: "error" });
    console.error("[BATTLE]", msg, e);
  }
}

function computePhase(state: BattleState): "OPENING" | "CONTACT" | "CLIMAX" | "ENDGAME" {
  // FR-018:委托给 tank-stats.computePhase,数值从 TANK_STATS 读
  // V3: 多坦克模式用团队平均 HP（取最差值的坦克）
  const selfHp = state.team_a?.tanks
    ? Math.min(...state.team_a.tanks.filter((t) => t.is_alive).map((t) => t.health))
    : state.self.health;
  const enemyHp = state.team_b?.tanks
    ? Math.min(...state.team_b.tanks.filter((t) => t.is_alive).map((t) => t.health))
    : state.enemy.health;
  const selfPos = state.team_a?.tanks?.[0]?.position ?? state.self.position;
  const enemyPos = state.team_b?.tanks?.[0]?.position ?? state.enemy.position;
  const maxHp = state.self.max_health || TANK_STATS.max_health;
  return computePhaseFromStats(selfHp, enemyHp, selfPos, enemyPos, maxHp, TANK_STATS.range);
}

/**
 * V2 LevelStore 格式(number[][]) → TileType[]
 * 避免 tiles 格式不一致导致 string[] vs number[][] 不一致。
 */
function v2LevelTiles(levelData: { map: { tiles: number[][] } }): TileType[] {
  const result: TileType[] = [];
  for (const row of levelData.map.tiles) {
    for (const cell of row) {
      result.push(NUM_TO_TILE[cell] || "empty");
    }
  }
  return result;
}

async function loadMapTiles(mapId: string): Promise<TileType[]> {
  // P0-#12 修复:从 data/maps/ 加载真实地图数据
  // FR-025(2026-07-06):接受 mapId 参数,屏 4 按 getEffectiveMapId(project) 选中的地图加载
  // 统一映射源 → tile-codec.ts CHAR_TO_TILE
  const map = getMapById(mapId);
  const tiles: TileType[] = [];
  for (const row of (map as any).tiles as string[]) {
    for (const ch of row) {
      tiles.push(CHAR_TO_TILE[ch] || "empty");
    }
  }
  return tiles;
}

/** BotAction type → CONSOLE tag */
function actionToConsoleTag(actionType: string): string | null {
  switch (actionType) {
    case "move": return "MOVE";
    case "shoot": return "SHOOT";
    case "scan": return "SCAN";
    case "idle": return null;
    default: return null;
  }
}

/** BotAction → CONSOLE 人类可读消息 */
function actionToConsoleMsg(action: BotAction, _team: "team_a" | "team_b"): string {
  const at: string = action.type;
  switch (at) {
    case "move": return `${t("battle.action_move_to")}(${(action as any).target.x}, ${(action as any).target.y})`;
    case "shoot": return t("battle.action_shoot");
    case "scan": return t("battle.action_scan");
    case "idle": return t("battle.action_idle");
    default: return at;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}
